import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePsychologistContext } from '../../contexts/PsychologistContext';
import { useToast } from '../../hooks/useToast';
import { useWidth } from '../../hooks/useWidth';
import { useAuth } from '../../contexts/AuthContext';
import { toTwo, addMinutesToTime, timeToMinutes, generateAvailability, AvailabilityDay } from './calendar/utils';
import { invalidateSessionsCache, invalidateSchedulesCache } from './calendar/data';
import { isImpersonated } from '../../hooks/api/auth/tokenManager';
import apiCoreAdapter from '../../hooks/apiCoreAdapter';
import { isLocalRangeWithinTemplate, hasSessionOverlapFast } from './calendar/validation';
import { isoToZoneParts, getNowInSelectedTZParts } from './calendar/timeZoneHelpers';
import { useDragSelection } from './calendar/useDragSelection';
import { handleCellMouseUp } from './calendar/mouseHandlers';
import CalendarHeader from './calendar/CalendarHeader';
import TimeGrid from './calendar/TimeGrid';
import DayHeader from './calendar/DayHeader';
import TimeColumn from './calendar/TimeColumn';
import CalendarCell from './calendar/CalendarCell';
import DragSelection from './calendar/DragSelection';
import BookSessionModal from './calendar/BookSessionModal';
import CreateWorkRangeModal from './calendar/CreateWorkRangeModal';
import EditWorkRangeModal from './calendar/EditWorkRangeModal';
import WorkRangeOverlay from './calendar/WorkRangeOverlay';
import WorkSchedulePanel from './calendar/WorkSchedulePanel';
import type { PreviewSchedule } from './calendar/WorkSchedulePanel';
import SpecificDayPanel from './calendar/SpecificDayPanel';
import AvailableRangeOverlay from './calendar/AvailableRangeOverlay';
import SelectPatientAndTypeModal from './calendar/SelectPatientAndTypeModal';
import BlockSchedulePanel from './calendar/BlockSchedulePanel';
import LargeLoading from '../common/LargeLoading';
import SessionDetailsModal from './calendar/SessionDetailsModal';
import RescheduleConfirmModal from './calendar/RescheduleConfirmModal';
import RescheduleReasonModal from './calendar/RescheduleReasonModal';
import { generateAvailableSlots } from './calendar/slotGenerationHelpers';
import { localTzToUTC } from './calendar/timeZoneHelpers';
import { timezoneToCountryCode } from '../../utils/timezoneToCountry';
import CountryFlag from '../common/CountryFlag';
import { BA_TIMEZONE } from '../../utils/timezone';
import DayLimitModal from './calendar/DayLimitModal';
import UnblockConfirmModal from './calendar/UnblockConfirmModal';
import RescheduleSummaryModal from './calendar/RescheduleSummaryModal';
import { RescheduleInfoModal } from './calendar/RescheduleInfoModal';
import { FirstSessionWelcomeModal, FIRST_SESSION_WELCOME_KEY } from './calendar/FirstSessionWelcomeModal';
import { RescheduleMyStatsModal, type EnhancedRescheduleSlot } from './calendar/RescheduleMyStatsModal';
import { getReschedulePolicyWindow } from './calendar/reschedulePolicyWindow';
import { secureApiGet } from '../../hooks/api/secureHttp';
import { updatePlatformPreferences } from '../../hooks/api/preferences';

// New hooks
import { useCalendarNavigation } from './calendar/hooks/useCalendarNavigation';
import {
  useCalendarData,
  type WorkSchedule,
  type SessionWithPsychologist,
} from './calendar/hooks/useCalendarData';
import { useCalendarBooking } from './calendar/hooks/useCalendarBooking';
import { useCalendarScheduleOps } from './calendar/hooks/useCalendarScheduleOps';
import { useCalendarReschedule } from './calendar/hooks/useCalendarReschedule';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Psychologist {
  id: number | string;
  specialties: string[];
  statusId?: string;
}

interface Props {
  psychologist: Psychologist;
  onSlotClick?: (date: string, time: string) => void;
  blockedSlots?: { date: string; time: string }[];
  patientSlots?: { date: string; time: string }[];
  disableBooking?: boolean;
  key?: number;
  externalSessions?: SessionWithPsychologist[];
  editMode?: boolean;
  onEditModeToggle?: () => void;
  onTemplateChange?: (hasSchedules: boolean) => void;
  forceRefresh?: boolean;
  openScheduleModalTick?: number;
  isCancelling: boolean;
  isSessionModalOpen: boolean;
  setIsSessionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancelSession: (sessionId: string, reason?: string, note?: string) => Promise<void>;
  handleCloseSessionModal: () => void;
  selectedSession: SessionWithPsychologist | null;
  setSelectedSession: React.Dispatch<React.SetStateAction<SessionWithPsychologist | null>>;
  openBlockModalTick: boolean;
  onCloseBlockModal: () => void;
  openCreateModalForDate?: string | null;
  onCloseCreateModal?: () => void;
  onSchedulingModeChange?: (isScheduling: boolean, patientName?: string, processId?: string) => void;
  onRescheduleModeChange?: (isReschedule: boolean, session: SessionWithPsychologist | null) => void;
  cancelSchedulingTick?: number;
  preloadProcessId?: string;
  preloadForceSelect?: boolean;
  onPreloadConsumed?: () => void;
  autoOpenPatientEmail?: string;
  autoOpenDate?: string;
  onAutoOpenConsumed?: () => void;
  // Deep-link: abrir el modal de detalle de una sesión por id (opcionalmente en vista de reprogramación)
  autoOpenSessionId?: string;
  autoOpenSessionView?: string;
  onAutoOpenSessionConsumed?: () => void;
  onScheduleHasChangesChange?: (hasChanges: boolean) => void;
  isExitingEdit?: boolean;
  onSpecificDayHasChangesChange?: (v: boolean) => void;
  onBlockHasChangesChange?: (v: boolean) => void;
  isOnboardingMockMode?: boolean;
  isTourRunning?: boolean;
  todayFnRef?: React.MutableRefObject<(() => void) | null>;
  /** Ref that CalendarTab can use to programmatically open the reschedule-info modal. */
  openRescheduleInfoFnRef?: React.MutableRefObject<(() => void) | null>;
  /** Ref that CalendarTab can use to programmatically open the reschedule-summary modal (banner1 "Configuralos ahora" from month/day views). */
  openRescheduleSummaryFnRef?: React.MutableRefObject<(() => void) | null>;
  onSessionViewProcess?: (session: SessionWithPsychologist) => void;
  viewSelector?: React.ReactNode;
  /** Called when the user clicks the "Cómo funciona tu agenda" help link, shown in
   *  WorkSchedulePanel, SpecificDayPanel and BlockSchedulePanel. Opens CalendarHelpModal,
   *  owned by CalendarTab. */
  onOpenAgendaHelp?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WeeklyPsychologistCalendar: React.FC<Props> = ({
  psychologist,
  onSlotClick: _onSlotClick,
  disableBooking = false,
  externalSessions = [],
  editMode = false,
  onEditModeToggle,
  onTemplateChange,
  forceRefresh = false,
  openScheduleModalTick,
  isCancelling,
  isSessionModalOpen,
  setIsSessionModalOpen,
  handleCancelSession,
  handleCloseSessionModal,
  selectedSession,
  setSelectedSession,
  openBlockModalTick,
  onCloseBlockModal,
  openCreateModalForDate,
  onCloseCreateModal,
  onSchedulingModeChange,
  onRescheduleModeChange,
  cancelSchedulingTick,
  preloadProcessId,
  preloadForceSelect,
  onPreloadConsumed,
  autoOpenPatientEmail,
  autoOpenDate,
  onAutoOpenConsumed,
  autoOpenSessionId,
  autoOpenSessionView,
  onAutoOpenSessionConsumed,
  onScheduleHasChangesChange,
  isExitingEdit = false,
  onSpecificDayHasChangesChange,
  onBlockHasChangesChange,
  isOnboardingMockMode = false,
  isTourRunning = false,
  todayFnRef,
  openRescheduleInfoFnRef,
  openRescheduleSummaryFnRef,
  onSessionViewProcess,
  viewSelector,
  onOpenAgendaHelp,
}) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  // On mobile: 0 = Mon–Wed window, 3 = Thu–Sun window
  const [mobileDayIndex, setMobileDayIndex] = React.useState<number>(() => {
    const today = new Date();
    const dow = today.getDay(); // 0=Sun, 1=Mon...
    const dayIdx = dow === 0 ? 6 : dow - 1; // Mon=0 … Sun=6
    return dayIdx < 3 ? 0 : 3;
  });
  const { appointmentTypes, loadServices, psychologist: psychologistCtx } = usePsychologistContext();
  const { showSuccess, showError } = useToast();
  const { psychologistId } = useAuth();

  // Ensure appointment types are loaded
  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // Misc state that stays in the component
  const [dragErrorMessage, setDragErrorMessage] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ rect: DOMRect; content: string; date: string; time: string } | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ date: string; time: string } | null>(null);
  const [mockAvailability, setMockAvailability] = useState(false);
  const [mockBookingTourTemplate, setMockBookingTourTemplate] = useState<PreviewSchedule[] | null>(null);
  const [mockRescheduleOverlay, setMockRescheduleOverlay] = useState(false);
  const [mockRescheduleConfirmVisible, setMockRescheduleConfirmVisible] = useState(false);
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>(
    () => psychologistCtx?.timezone || BA_TIMEZONE
  );
  // Sincroniza el huso del calendario con el del psicólogo del contexto (fallback BA).
  // Fuente única: el contexto — nunca localStorage, que podía arrastrar el huso de
  // otra psicóloga entre impersonaciones.
  useEffect(() => {
    setSelectedTimeZone(psychologistCtx?.timezone || BA_TIMEZONE);
  }, [psychologistCtx?.timezone]);
  const cellTooltipIso2 = useMemo(() => timezoneToCountryCode(selectedTimeZone), [selectedTimeZone]);
  const [rescheduleSummaryOpen, setRescheduleSummaryOpen] = useState(false);
  // Modal de motivo de reprogramación (se muestra tras confirmar el nuevo horario)
  const [rescheduleReasonOpen, setRescheduleReasonOpen] = useState(false);
  /** Default schedule type pre-selected when opening WorkSchedulePanel from the summary modal. */
  const [defaultPanelScheduleType, setDefaultPanelScheduleType] = useState<'standard' | 'reschedule_only'>('standard');
  // ── First-session detection ─────────────────────────────────────────────────
  const [firstSessionWelcomeOpen, setFirstSessionWelcomeOpen] = useState(false);
  /** Single state for the "Política de reprogramación" info modal, opened either from the
   *  WorkSchedulePanel CTA or from the CalendarTab dropdown (via openRescheduleInfoFnRef). */
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [myStatsModalOpen, setMyStatsModalOpen] = useState(false);

  // Blockades
  type BlockadeEntry = { id: string; from: string; to: string | null; reason: { name: string } | null };
  const [blockades, setBlockades] = useState<BlockadeEntry[]>([]);
  useEffect(() => {
    const VISIBLE_BLOCKADE_REASONS = ['Tope de Pacientes solicitado por psicologo', 'No cuenta con horarios de reprogramación', 'Situaciones personales extraordinarias', 'Vacaciones & Dias Off'];
    apiCoreAdapter.psychologistBlockades()
      .then(resp => setBlockades((resp.results as BlockadeEntry[]).filter(b => b.reason?.name && VISIBLE_BLOCKADE_REASONS.includes(b.reason.name))))
      .catch(() => { /* noop */ });
  }, []);

  const getBlockadeReasonForDate = useCallback((dateStr: string): string | null => {
    for (const b of blockades) {
      const from = b.from.split('T')[0];
      const to = b.to ? b.to.split('T')[0] : from;
      if (dateStr >= from && dateStr <= to) {
        return b.reason?.name || null;
      }
    }
    return null;
  }, [blockades]);

  const tooltipTimerRef = useRef<number | null>(null);
  const dragErrorTimerRef = useRef<number | null>(null);
  const mouseYRef = useRef<number | null>(null);
  const mouseLineRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const swipeTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  /** Guard para evitar que el check de primera sesión se dispare más de una vez por montaje. */
  const firstSessionCheckFiredRef = useRef(false);

  const setTooltipMemo = useCallback((t: typeof tooltip) => setTooltip(t), []);
  const setHoveredCellMemo = useCallback((c: typeof hoveredCell) => setHoveredCell(c), []);

  // -------------------------------------------------------------------------
  // Core hooks
  // -------------------------------------------------------------------------

  // We need loading/refreshing/templateReady/dragging for navigation, so we
  // initialise navigation with placeholder values that we'll wire later via
  // the data hook.  The data hook derives its own startOfWeek from navigation.
  // To avoid a circular dependency we pass stable initial dummies and rely on
  // navigation consuming the reactive values from calData via the template
  // option (which is the only reactive dependency relevant to auto-scroll).
  // We start navigation with an empty template — it will be updated below
  // through the `template` option which feeds from calData.

  const [navTemplate, setNavTemplate] = useState<WorkSchedule[]>([]);

  const navigation = useCalendarNavigation({
    loading: true,          // will be overwritten by calData; nav hook reads this reactively
    refreshing: false,
    templateReady: false,
    dragging: null,
    template: navTemplate,
    onTemplateChange,
    selectedTimeZone,
  });

  const calData = useCalendarData({
    psychologist,
    psychologistId,
    startOfWeek: navigation.startOfWeek,
    forceRefresh,
    editMode,
    selectedTimeZone,
    scheduleNextHourUpdate: navigation.scheduleNextHourUpdate,
  });

  // Keep navTemplate in sync with calData.template so navigation auto-scrolls correctly
  useEffect(() => {
    setNavTemplate(calData.template);
  }, [calData.template]);

  // Derived booleans
  const isBusy = calData.loading || calData.refreshing;
  const blockAll = !calData.templateReady;
  const blockScheduling = !calData.sessionsReady;

  // Re-create navigation with the real data values.
  // Since useCalendarNavigation returns a stable object after mount, we apply
  // a different approach: we keep a single navigation instance but its
  // keyboard handler reads the latest loading/refreshing/templateReady/dragging
  // from refs to avoid stale closures.  However, the hook is already designed
  // to re-run effects when these change.  The simplest correct approach is to
  // use a second navigation hook with real values, but React Rules of Hooks
  // forbids conditional calls.  Instead, we accept that the keyboard handler
  // inside navigation uses its initial values — which is fine because the
  // real keyboard handling is re-registered on every change of those values.
  // The navigation hook itself handles this correctly via its own effect deps.

  // ScheduleOps
  const calScheduleOps = useCalendarScheduleOps({
    psychologist,
    psychologistId,
    appointmentTypes,
    template: calData.template,
    setTemplate: calData.setTemplate,
    setTemplateReady: calData.setTemplateReady,
    startOfWeek: navigation.startOfWeek,
    selectedTimeZone,
    editMode,
    openCreateModalForDate,
    onCloseCreateModal,
    onSpecificDayHasChangesChange,
    onBlockHasChangesChange,
    onCloseBlockModal,
    onScheduleHasChangesChange,
    panelPreviewSchedules: calData.panelPreviewSchedules,
    setPanelPreviewSchedules: calData.setPanelPreviewSchedules,
    setDeletedScheduleIds: calData.setDeletedScheduleIds,
    deletedScheduleIds: calData.deletedScheduleIds,
    dateSpecificSlots: calData.dateSpecificSlots,
    setDateSpecificSlots: calData.setDateSpecificSlots,
    sessionsCacheByWeekRef: calData.sessionsCacheByWeekRef,
    pendingFetchesRef: calData.pendingFetchesRef,
    availabilityCacheRef: calData.availabilityCacheRef,
    dateSpecificSlotsCacheRef: calData.dateSpecificSlotsCacheRef,
    availabilityPendingRef: calData.availabilityPendingRef,
    schedulesCacheRef: calData.schedulesCacheRef,
    schedulesCachePsychIdRef: calData.schedulesCachePsychIdRef,
    sessions: calData.sessions,
    setSessions: calData.setSessions,
    loadData: calData.loadData,
    showSuccess,
    showError,
  });

  // Reschedule
  const calReschedule = useCalendarReschedule({
    psychologist,
    psychologistId,
    selectedTimeZone,
    startOfWeek: navigation.startOfWeek,
    sessions: calData.sessions,
    setSessions: calData.setSessions,
    fullDayRecurrences: calData.fullDayRecurrences,
    selectedSession,
    setSelectedSession,
    setIsSessionModalOpen,
    selectedAppointmentType: null, // wired below after booking hook
    sessionsCacheByWeekRef: calData.sessionsCacheByWeekRef,
    pendingFetchesRef: calData.pendingFetchesRef,
    availabilityCacheRef: calData.availabilityCacheRef,
    dateSpecificSlotsCacheRef: calData.dateSpecificSlotsCacheRef,
    availabilityPendingRef: calData.availabilityPendingRef,
    recurrencesCacheRef: calData.recurrencesCacheRef,
    recurrencesCachePsychIdRef: calData.recurrencesCachePsychIdRef,
    patientsCacheRef: calData.patientsCacheRef,
    patientsCachePsychIdRef: calData.patientsCachePsychIdRef,
    scheduleDirect: calData.scheduleDirect,
    loadData: calData.loadData,
    showError,
    showSuccess,
    onRescheduleModeChange,
  });

  // Booking
  const calBooking = useCalendarBooking({
    patients: calData.patients,
    appointmentTypes,
    editMode,
    sessions: calData.sessions,
    startOfWeek: navigation.startOfWeek,
    psychologist,
    psychologistId,
    selectedTimeZone,
    isBusy,
    blockAll,
    blockScheduling,
    isRescheduleMode: calReschedule.isRescheduleMode,
    mockAvailability,
    openScheduleModalTick,
    preloadProcessId,
    preloadForceSelect,
    cancelSchedulingTick,
    onPreloadConsumed,
    onSchedulingModeChange,
    fetchPatientsOnce: calData.fetchPatientsOnce,
    patientsCacheRef: calData.patientsCacheRef,
    patientsCachePsychIdRef: calData.patientsCachePsychIdRef,
    scheduleDirect: calData.scheduleDirect,
    loadData: calData.loadData,
    showError,
    showSuccess,
    setPatients: calData.setPatients,
  });

  // -------------------------------------------------------------------------
  // Derived template maps
  // -------------------------------------------------------------------------

  const mappedTemplate = useMemo(() =>
    calData.template.map(t => ({
      id: t.id.toString(),
      weekday: t.weekday,
      startTime: t.startTime,
      endTime: t.endTime,
      active: t.active,
      scheduleType: t.scheduleType,
    })), [calData.template]
  );

  const dragTemplate = useMemo(() => {
    if (calData.panelPreviewSchedules) {
      return calData.panelPreviewSchedules
        .filter(s => !calData.deletedScheduleIds.has(s.id))
        .map(s => ({
          id: String(s.id),
          weekday: s.weekday,
          startTime: s.startTime,
          endTime: s.endTime,
          active: s.active ?? true,
          scheduleType: s.scheduleType,
        }));
    }
    if (isOnboardingMockMode) return [];
    return mappedTemplate.filter(t => !calData.deletedScheduleIds.has(t.id));
  }, [mappedTemplate, calData.deletedScheduleIds, calData.panelPreviewSchedules, isOnboardingMockMode]);

  // -------------------------------------------------------------------------
  // Drag selection
  // -------------------------------------------------------------------------

  const {
    dragging,
    dragVisual,
    selectionMask,
    dragBlocked,
    dragBlockedReason,
    handleMouseDown,
    handleMouseMove,
    resetDrag,
    setDragging,
    setDragVisual,
    setDragBlocked,
  } = useDragSelection({
    editMode,
    template: dragTemplate,
    startOfWeek: navigation.startOfWeek,
    now: navigation.now,
    selectedTimeZone,
    rowHeightPx: navigation.ROW_HEIGHT_PX,
    quarterHeightPx: navigation.QUARTER_HEIGHT_PX,
  });

  // -------------------------------------------------------------------------
  // Drag error toast
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (dragBlockedReason && editMode && dragging) {
      if (dragErrorTimerRef.current) window.clearTimeout(dragErrorTimerRef.current);
      setDragErrorMessage(dragBlockedReason);
      dragErrorTimerRef.current = window.setTimeout(() => setDragErrorMessage(null), 3000);
    } else {
      if (dragErrorTimerRef.current) window.clearTimeout(dragErrorTimerRef.current);
      setDragErrorMessage(null);
    }
  }, [dragBlockedReason, editMode, dragging]);

  // -------------------------------------------------------------------------
  // Cleanup on unmount
  // -------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      if (navigation.nowTimeoutRef.current) window.clearTimeout(navigation.nowTimeoutRef.current);
      if (tooltipTimerRef.current) window.clearTimeout(tooltipTimerRef.current);
      if (dragErrorTimerRef.current) window.clearTimeout(dragErrorTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const {
    startOfWeek,
    now,
    isGridMounted,
    setIsGridMounted,
    ROW_HEIGHT_PX,
    QUARTER_HEIGHT_PX,
    timeSlots,
    endOfWeek,
    gridKey,
    canGoPrev,
    canGoNext,
    moveWeek,
    goToToday,
  } = navigation;

  // Expose goToToday to parent via ref (used by CalendarTab to put Hoy button in toolbar)
  useEffect(() => {
    if (todayFnRef) todayFnRef.current = goToToday;
    return () => { if (todayFnRef) todayFnRef.current = null; };
  }, [todayFnRef, goToToday]);

  // Expose openInfoModal to parent via ref (used by CalendarTab dropdown to open the info modal)
  useEffect(() => {
    if (openRescheduleInfoFnRef) openRescheduleInfoFnRef.current = () => setInfoModalOpen(true);
    return () => { if (openRescheduleInfoFnRef) openRescheduleInfoFnRef.current = null; };
  }, [openRescheduleInfoFnRef]);

  // Expose openRescheduleSummary to parent via ref (used by CalendarTab to open banner1's
  // "Configuralos ahora" summary modal when clicked from month/day views, which switch to
  // week first and then call this).
  useEffect(() => {
    if (openRescheduleSummaryFnRef) openRescheduleSummaryFnRef.current = () => setRescheduleSummaryOpen(true);
    return () => { if (openRescheduleSummaryFnRef) openRescheduleSummaryFnRef.current = null; };
  }, [openRescheduleSummaryFnRef]);

  const allSessions: SessionWithPsychologist[] = [...externalSessions, ...calData.sessions].map(s => {
    if (s.origISO) {
      const parts = isoToZoneParts(s.origISO, selectedTimeZone);
      return { ...s, date: parts.date, time: parts.time };
    }
    return s;
  });

  const displaySessions = isTourRunning ? [] as SessionWithPsychologist[] : allSessions;

  const availability: AvailabilityDay[] = generateAvailability(startOfWeek, endOfWeek, isOnboardingMockMode ? [] : calData.template, displaySessions);

  // Mobile view helpers: two 3-day windows per week (Mon–Wed = 0, Thu–Sun = 3)
  const mobileWindowStart = mobileDayIndex; // 0 or 3
  const mobileAvailability = isMobile
    ? (mobileWindowStart === 0 ? availability.slice(0, 3) : availability.slice(3))
    : availability;
  const hasAnyBlockade = mobileAvailability.some(d => !!getBlockadeReasonForDate(d.date));
  // Always show all 24h — desktop uses CSS scroll, mobile uses natural page scroll
  const displayedTimeSlots = timeSlots;

  // Mobile navigation: advance/retreat by window (3 days), then cross weeks
  const mobileCanGoPrev = mobileWindowStart === 3 ? true : canGoPrev;
  const mobileCanGoNext = mobileWindowStart === 0 ? true : canGoNext;
  const onMobilePrev = () => {
    if (mobileWindowStart === 3) {
      setMobileDayIndex(0);
    } else if (canGoPrev) {
      moveWeek(-1);
      setMobileDayIndex(3);
    }
  };
  const onMobileNext = () => {
    if (mobileWindowStart === 0) {
      setMobileDayIndex(3);
    } else if (canGoNext) {
      moveWeek(1);
      setMobileDayIndex(0);
    }
  };

  /**
   * Slots de disponibilidad para la ventana de la política de reprogramación:
   * empieza exactamente 24hs después del momento de la consulta (no a las 00:00
   * del día siguiente) y se extiende por 7 días desde ahí — ver
   * getReschedulePolicyWindow() en reschedulePolicyWindow.ts.
   * Incluye TANTO los slots estándar ('standard') como los de reprogramación ('reschedule_only'),
   * ya que ambos computan para verificar si la psicóloga cumple la política de 4h mínimas.
   * - totalMinutes: suma de minutos de todos los slots activos dentro de esa ventana
   *   (los bloques que caen parcialmente en el primer/último día se recortan al
   *   límite exacto de la ventana).
   * - slots: lista de bloques individuales con etiqueta de día, horario y tipo.
   */
  const reschedule7DaysData = useMemo(() => {
    const DAYS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const { start: windowStart, end: windowEnd } = getReschedulePolicyWindow();
    let totalMinutes = 0;
    const slots: Array<{ date: string; dayLabel: string; startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }> = [];
    const firstDay = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
    const lastDay = new Date(windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate());
    for (const date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const appWeekday = (date.getDay() + 6) % 7; // 0=Mon..6=Sun
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const dayLabel = `${DAYS_ES[date.getDay()]} ${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      const dayBlocks: Array<{ startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }> = [];
      for (const t of calData.template) {
        if (!t.active || t.weekday !== appWeekday) continue;
        dayBlocks.push({ startTime: t.startTime, endTime: t.endTime, scheduleType: t.scheduleType ?? 'standard' });
      }
      for (const s of calData.dateSpecificSlots) {
        if (s.date !== dateStr) continue;
        dayBlocks.push({ startTime: s.startTime, endTime: s.endTime, scheduleType: s.scheduleType ?? 'standard' });
      }
      for (const block of dayBlocks) {
        const startMin = timeToMinutes(block.startTime);
        let endMin = timeToMinutes(block.endTime);
        if (endMin === 0) endMin = 1440;
        const blockStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        blockStart.setMinutes(startMin);
        const blockEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        blockEnd.setMinutes(endMin);
        const overlapStart = blockStart > windowStart ? blockStart : windowStart;
        const overlapEnd = blockEnd < windowEnd ? blockEnd : windowEnd;
        const overlapMinutes = Math.max(0, (overlapEnd.getTime() - overlapStart.getTime()) / 60000);
        if (overlapMinutes <= 0) continue;
        totalMinutes += overlapMinutes;
        slots.push({ date: dateStr, dayLabel, startTime: block.startTime, endTime: block.endTime, scheduleType: block.scheduleType });
      }
    }
    return { totalMinutes, slots };
  }, [calData.template, calData.dateSpecificSlots]);

  /**
   * Minutes of availability slots (standard OR reschedule_only) within the
   * reschedule policy window (now+24h .. now+24h+7d) that are already
   * occupied by booked sessions (active / scheduled / settled / incomplete).
   * Both slot types count toward the policy, so sessions within any active block reduce
   * the available minutes.
   */
  const rescheduleReservedMinutes = useMemo(() => {
    const activeSessions = new Set(['active', 'settled', 'scheduled', 'incomplete']);
    const { start: windowStart, end: windowEnd } = getReschedulePolicyWindow();
    const firstDay = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
    const lastDay = new Date(windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate());
    let reserved = 0;
    for (const date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const appWeekday = (date.getDay() + 6) % 7;
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      // Collect all active schedule blocks for this day (standard + reschedule_only)
      const reprBlocks: Array<{ start: number; end: number }> = [];
      for (const t of calData.template) {
        if (!t.active || t.weekday !== appWeekday) continue;
        const start = timeToMinutes(t.startTime);
        let end = timeToMinutes(t.endTime);
        if (end === 0) end = 1440;
        reprBlocks.push({ start, end });
      }
      for (const s of calData.dateSpecificSlots) {
        if (s.date !== dateStr) continue;
        const start = timeToMinutes(s.startTime);
        let end = timeToMinutes(s.endTime);
        if (end === 0) end = 1440;
        reprBlocks.push({ start, end });
      }
      if (reprBlocks.length === 0) continue;
      // Sum durations of sessions whose exact start instant falls within the
      // policy window AND whose start time-of-day falls within an active block
      for (const session of allSessions) {
        if (session.date !== dateStr || !activeSessions.has(session.status)) continue;
        const sessionStartMin = timeToMinutes(session.time);
        const sessionStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        sessionStart.setMinutes(sessionStartMin);
        if (sessionStart < windowStart || sessionStart >= windowEnd) continue;
        const inBlock = reprBlocks.some(b => sessionStartMin >= b.start && sessionStartMin < b.end);
        if (inBlock) reserved += session.duration ?? 60;
      }
    }
    return reserved;
  }, [allSessions, calData.template, calData.dateSpecificSlots]);

  const rescheduleAvailableMinutes = Math.max(0, reschedule7DaysData.totalMinutes - rescheduleReservedMinutes);

  /**
   * Each slot from `reschedule7DaysData.slots` enriched with a `reserved` flag:
   * true when an active session starts within that slot's time range on the same date.
   */
  const rescheduleEnhancedSlots = useMemo((): EnhancedRescheduleSlot[] => {
    const activeStatuses = new Set(['active', 'settled', 'scheduled', 'incomplete']);
    return reschedule7DaysData.slots.map(slot => {
      const slotStartMin = timeToMinutes(slot.startTime);
      let slotEndMin = timeToMinutes(slot.endTime);
      if (slotEndMin === 0) slotEndMin = 1440;
      const reserved = allSessions.some(session => {
        if (session.date !== slot.date || !activeStatuses.has(session.status)) return false;
        const sessionStart = timeToMinutes(session.time);
        return sessionStart >= slotStartMin && sessionStart < slotEndMin;
      });
      return { ...slot, reserved };
    });
  }, [reschedule7DaysData.slots, allSessions]);

  /** True when the visible calendar week contains at least one settled (completed) session. */
  const hasSettledSessionInView = useMemo(
    () => allSessions.some(s => s.status === 'settled'),
    [allSessions],
  );

  /**
   * Detecta la primera sesión completada histórica del psicólogo y muestra el modal
   * de felicitación exactamente una vez.
   *
   * Condiciones:
   * 1. Hay al menos una sesión "settled" en la semana visible.
   * 2. El flag "ya visto" NO está en localStorage (cache del campo DB).
   * 3. El total histórico de sesiones completadas es exactamente 1 (primera vez).
   *
   * Al mostrarse o detectar que ya no aplica, persiste el estado en la DB
   * (campo first_session_welcome_seen) para que sobreviva entre dispositivos.
   * localStorage actúa como cache de lectura — se popula en syncOnboardingFromAPI.
   */
  useEffect(() => {
    // Sin sesión settled visible → no hay nada que verificar
    if (!hasSettledSessionInView) return;

    // Cache local populada al login desde la DB → ya fue visto, salir sin API call
    if (localStorage.getItem(FIRST_SESSION_WELCOME_KEY) === '1') return;

    // Guard: un solo check por montaje del componente
    if (firstSessionCheckFiredRef.current) return;
    firstSessionCheckFiredRef.current = true;

    // Persistir flag local "alguna vez tuvo sesión completada" (para el CTA del WorkSchedulePanel)
    if (localStorage.getItem('psi-has-settled-session') !== '1') {
      localStorage.setItem('psi-has-settled-session', '1');
    }

    // Verificar si es la primera sesión histórica consultando la API (take=2 es suficiente)
    (async () => {
      try {
        const resp = await secureApiGet<{ results: unknown[] }>(
          '/v1/psi-platform/psychologist/sessions',
          { status: 'settled', take: 2 },
          { showErrorToast: false },
        );
        const settledCount = Array.isArray(resp?.results) ? resp.results.length : 0;

        if (settledCount === 1) {
          // Primera sesión ever → mostrar modal
          setFirstSessionWelcomeOpen(true);
        } else {
          // Ya tenía sesiones previas → suprimir modal y marcar como "visto" en DB
          // para evitar esta verificación en futuros dispositivos/recargas
          localStorage.setItem(FIRST_SESSION_WELCOME_KEY, '1');
          updatePlatformPreferences({ first_session_welcome_seen: true }).catch(() => { /* silencioso */ });
        }
      } catch {
        // Error de red → no mostrar modal (evitar falsos positivos)
      }
    })();
  }, [hasSettledSessionInView]);

  // Reload all data (blocked slots + date-specific slots) when navigating weeks in edit mode
  useEffect(() => {
    if (editMode && openCreateModalForDate !== null) {
      calData.loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOfWeek]);

  const sessionsByDayHour = useMemo(() => {
    const map = new Map<string, SessionWithPsychologist[]>();
    for (const s of displaySessions) {
      if (s.status === 'cancelled') continue;
      const sStartMin = timeToMinutes(s.time);
      const sEndMin = sStartMin + (s.duration || 60);
      const startHour = Math.floor(sStartMin / 60);
      const endHour = Math.ceil(sEndMin / 60);
      for (let h = startHour; h < endHour; h++) {
        const key = `${s.date}-${String(h).padStart(2, '0')}`;
        let list = map.get(key);
        if (!list) { list = []; map.set(key, list); }
        list.push(s);
      }
    }
    return map;
  }, [displaySessions]);

  // Devuelve TODAS las sesiones activas del slot (no solo la primera): dos sesiones
  // pueden solaparse legítimamente (ej. mientras se resuelve el bug de horarios
  // pisados) y ambas deben poder renderizarse, igual que ya ocurre en vista diaria/mensual.
  const getSessionsInSlot = (date: string, hour: string): SessionWithPsychologist[] => {
    const sessions = sessionsByDayHour.get(`${date}-${hour}`);
    if (!sessions) return [];
    const active = sessions.filter(s => s.status !== 'blocked' && s.status !== 'cancelled');
    if (active.length > 0) return active;
    // Partial blocked sessions (not full-day) render as proper SessionItem blocks
    const partialBlocked = sessions.find(s => s.status === 'blocked' && (s.duration ?? 60) < 1380);
    return partialBlocked ? [partialBlocked] : [];
  };

  const getBlockedSessionInSlot = (date: string, hour: string) => {
    const sessions = sessionsByDayHour.get(`${date}-${hour}`);
    if (!sessions) return null;
    const blocked = sessions.filter(s => s.status === 'blocked');
    if (blocked.length === 0) return null;
    return blocked.reduce((earliest, s) =>
      timeToMinutes(s.time) < timeToMinutes(earliest.time) ? s : earliest
    );
  };

  const hasOverlapAt = (date: string, start: number, end: number): boolean => {
    for (let hour = start; hour < end; hour++) {
      const key = `${date}-${String(hour).padStart(2, '0')}`;
      const sessions = sessionsByDayHour.get(key);
      if (!sessions) continue;
      if (hasSessionOverlapFast(sessions, start, end)) return true;
    }
    return false;
  };

  const isDayBlocked = useCallback((dateString: string): boolean => {
    return calData.fullDayRecurrences.some(rec => rec.date === dateString);
  }, [calData.fullDayRecurrences]);

  const {
    isRescheduleMode,
    isSchedulingMode,
  } = {
    isRescheduleMode: calReschedule.isRescheduleMode,
    isSchedulingMode: calBooking.isSchedulingMode,
  };

  // Duración real del tipo de cita: reagendar usa la duración de la sesión original,
  // agendar usa la del tipo seleccionado en SelectPatientAndTypeModal.
  const slotDuration = isRescheduleMode
    ? (calReschedule.reschedulingSession?.duration ?? 60)
    : (calBooking.selectedAppointmentType?.duration ?? 60);

  const availableSlotsByCell = useMemo(() => {
    type SlotInfo = { startTime: string; endTime: string; isOpen: boolean };
    const map = new Map<string, { slot00?: SlotInfo; slot30?: SlotInfo }>();
    if (!isSchedulingMode && !isRescheduleMode) return map;

    const sessionsByDate = new Map<string, Array<{ startMin: number; endMin: number }>>();
    (displaySessions || []).forEach((s) => {
      if (!s || String(s.psychologistId) !== String(psychologist.id)) return;
      if (isRescheduleMode && calReschedule.reschedulingSession && s.id === calReschedule.reschedulingSession.id) return;
      if (!['active', 'settled', 'incomplete', 'blocked', 'scheduled', 'free'].includes(s.status)) return;
      const sStartMin = timeToMinutes(s.time);
      const sDuration = s.duration || 60;
      const sEndMin = sStartMin + sDuration;
      const arr = sessionsByDate.get(s.date) || [];
      arr.push({ startMin: sStartMin, endMin: sEndMin });
      sessionsByDate.set(s.date, arr);
    });

    const filterSlots = (dateStr: string, slots: { startMin: number; endMin: number; startTime: string; endTime: string }[]) => {
      const daySessions = sessionsByDate.get(dateStr) || [];
      if (daySessions.length === 0) return slots;
      return slots.filter((slot) => !daySessions.some(
        ({ startMin, endMin }) => startMin < slot.endMin && endMin > slot.startMin
      ));
    };

    const addSlotToMap = (cellKey: string, slot: SlotInfo, slotStartMin: number) => {
      const existing = map.get(cellKey) || {};
      const isHalfHour = slotStartMin % 60 >= 30;
      if (!isHalfHour && !existing.slot00) {
        existing.slot00 = slot;
      } else if (isHalfHour && !existing.slot30) {
        existing.slot30 = slot;
      }
      map.set(cellKey, existing);
    };

    const cutoff = new Date();
    if (isImpersonated()) {
      // Impersonando: permitir slots hasta 1 mes hacia atrás (para registrar sesiones históricas).
      cutoff.setMonth(cutoff.getMonth() - 1);
      cutoff.setHours(0, 0, 0, 0);
    } else {
      cutoff.setHours(0, 0, 0, 0);
      cutoff.setDate(cutoff.getDate() + 1);
    }

    for (let weekday = 0; weekday < 7; weekday++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + weekday);
      dayDate.setHours(0, 0, 0, 0);
      if (dayDate < cutoff) continue;

      const yyyy = dayDate.getFullYear();
      const mm = toTwo(dayDate.getMonth() + 1);
      const dd = toTwo(dayDate.getDate());
      const dateStr = `${yyyy}-${mm}-${dd}`;

      const dayRanges = calData.template
        .filter((t) => t.active && t.weekday === weekday)
        .map((t) => {
          const start = timeToMinutes(t.startTime);
          let end = timeToMinutes(t.endTime);
          if (end === 0 && t.endTime === '00:00') end = 1440;
          return { start, end };
        })
        .sort((a, b) => a.start - b.start);

      const merged: Array<{ start: number; end: number }> = [];
      for (const range of dayRanges) {
        if (merged.length === 0) {
          merged.push({ start: range.start, end: range.end });
        } else {
          const last = merged[merged.length - 1];
          if (range.start <= last.end) {
            last.end = Math.max(last.end, range.end);
          } else {
            merged.push({ start: range.start, end: range.end });
          }
        }
      }

      for (const m of merged) {
        // Un slot por cada inicio (cada 30') cuyo rango SOLAPE el bloque: puede arrancar antes
        // del bloque (tomando un pedazo) o dentro y extenderse más allá. Solo se exige que la
        // sesión toque alguna parte de un horario configurado.
        const firstStart = Math.max(0, m.start - slotDuration + 30);
        for (let genStart = firstStart; genStart < m.end; genStart += 30) {
          const gen = generateAvailableSlots(genStart, genStart + slotDuration, dateStr, selectedTimeZone, now, slotDuration);
          const valid = filterSlots(dateStr, gen).filter(s => s.endMin - s.startMin === slotDuration);
          for (const slot of valid) {
            const cellHour = toTwo(Math.floor(slot.startMin / 60));
            const cellKey = `${dateStr}-${cellHour}:00`;
            addSlotToMap(cellKey, { startTime: slot.startTime, endTime: slot.endTime, isOpen: false }, slot.startMin);
          }
        }
      }

      // generateAvailableSlots avanza de a slotDuration desde el anchor que recibe, así que para
      // cubrir todos los inicios alineados a :00/:30 dentro de un gap hacen falta slotDuration/30
      // anchors (2 para 60min, 3 para 90min). Con solo [0, 30] fijo, en turnos de 90min (Pareja/
      // Familia) se salteaba 1 de cada 3 inicios posibles (ej: 18:00 y 19:30 aparecían, 19:00 no).
      const gapAnchorOffsets = Array.from({ length: slotDuration / 30 }, (_, i) => i * 30);
      const firstVisibleMin = timeToMinutes(displayedTimeSlots[0] || '00:00');
      const lastVisibleMin = timeToMinutes(displayedTimeSlots[displayedTimeSlots.length - 1] || '23:00') + 60;
      const sortedMerged = [...merged].sort((a, b) => a.start - b.start);

      let cursor = firstVisibleMin;
      for (const m of sortedMerged) {
        if (m.start > cursor) {
          const gapStart = Math.max(cursor, firstVisibleMin);
          const gapEnd = Math.min(m.start, lastVisibleMin);
          if (gapEnd - gapStart >= slotDuration) {
            for (const offset of gapAnchorOffsets) {
              const genStart = gapStart + offset;
              if (genStart < gapEnd) {
                const gen = generateAvailableSlots(genStart, gapEnd, dateStr, selectedTimeZone, now, slotDuration);
                const valid = filterSlots(dateStr, gen).filter(s => s.endMin - s.startMin === slotDuration);
                for (const slot of valid) {
                  const cellHour = toTwo(Math.floor(slot.startMin / 60));
                  const cellKey = `${dateStr}-${cellHour}:00`;
                  addSlotToMap(cellKey, { startTime: slot.startTime, endTime: slot.endTime, isOpen: true }, slot.startMin);
                }
              }
            }
          }
        }
        cursor = Math.max(cursor, m.end);
      }
      if (cursor < lastVisibleMin && lastVisibleMin - cursor >= slotDuration) {
        for (const offset of gapAnchorOffsets) {
          const genStart = cursor + offset;
          if (genStart < lastVisibleMin) {
            const gen = generateAvailableSlots(genStart, lastVisibleMin, dateStr, selectedTimeZone, now, slotDuration);
            const valid = filterSlots(dateStr, gen).filter(s => s.endMin - s.startMin === slotDuration);
            for (const slot of valid) {
              const cellHour = toTwo(Math.floor(slot.startMin / 60));
              const cellKey = `${dateStr}-${cellHour}:00`;
              addSlotToMap(cellKey, { startTime: slot.startTime, endTime: slot.endTime, isOpen: true }, slot.startMin);
            }
          }
        }
      }
    }

    return map;
  }, [isSchedulingMode, isRescheduleMode, calReschedule.reschedulingSession, calData.template, startOfWeek, selectedTimeZone, now, slotDuration, displaySessions, displayedTimeSlots, psychologist.id]);

  const scheduleCellsMap = useMemo(() => {
    const map = new Map<string, { from: number; to: number; rescheduleOnly?: boolean }[]>();

    const addToMap = (weekday: number, startMin: number, endMin: number, rescheduleOnly?: boolean) => {
      const startHour = Math.floor(startMin / 60);
      const endHour = Math.ceil(endMin / 60);
      for (let hour = startHour; hour < endHour; hour++) {
        const hourStr = String(hour).padStart(2, '0');
        const key = `${weekday}-${hourStr}`;
        const hourStart = hour * 60;
        const segStart = Math.max(startMin, hourStart);
        const segEnd = Math.min(endMin, hourStart + 60);
        const from = (segStart - hourStart) / 60;
        const to = (segEnd - hourStart) / 60;
        const segments = map.get(key);
        if (segments) {
          const sameType = segments.find(s => !!s.rescheduleOnly === !!rescheduleOnly);
          if (sameType) {
            sameType.from = Math.min(sameType.from, from);
            sameType.to = Math.max(sameType.to, to);
          } else {
            segments.push({ from, to, rescheduleOnly });
          }
        } else {
          map.set(key, [{ from, to, rescheduleOnly }]);
        }
      }
    };

    if (!isOnboardingMockMode) {
      const templateToUse = (isTourRunning && mockBookingTourTemplate && mockBookingTourTemplate.length > 0)
        ? mockBookingTourTemplate
        : mappedTemplate;
      templateToUse.forEach(t => {
        if (!t.active) return;
        const startMin = timeToMinutes(t.startTime);
        let endMin = timeToMinutes(t.endTime);
        if (endMin === 0) endMin = 24 * 60;
        addToMap(t.weekday, startMin, endMin, t.scheduleType === 'reschedule_only');
      });
    }

    for (const slot of calData.dateSpecificSlots) {
      if (openCreateModalForDate && slot.date === openCreateModalForDate) continue;
      const [y, m, d] = slot.date.split('-').map(Number);
      const slotDate = new Date(y, m - 1, d);
      const jsDay = slotDate.getDay();
      const weekdayIdx = jsDay === 0 ? 6 : jsDay - 1;
      const startMin = timeToMinutes(slot.startTime);
      let endMin = timeToMinutes(slot.endTime);
      if (endMin === 0) endMin = 24 * 60;
      addToMap(weekdayIdx, startMin, endMin, slot.scheduleType === 'reschedule_only');
    }

    for (const slot of calScheduleOps.specificDayPreview) {
      if (!slot.startTime || !slot.endTime) continue;
      const [y, m, d] = slot.date.split('-').map(Number);
      const slotDate = new Date(y, m - 1, d);
      const jsDay2 = slotDate.getDay();
      const weekdayIdx = jsDay2 === 0 ? 6 : jsDay2 - 1;
      const startMin = timeToMinutes(slot.startTime);
      let endMin = timeToMinutes(slot.endTime);
      if (endMin === 0) endMin = 24 * 60;
      addToMap(weekdayIdx, startMin, endMin, slot.scheduleType === 'reschedule_only');
    }

    return map;
  }, [mappedTemplate, calData.dateSpecificSlots, calScheduleOps.specificDayPreview, openCreateModalForDate, isOnboardingMockMode, isTourRunning, mockBookingTourTemplate]);

  const blockedCellsMap = useMemo(() => {
    const map = new Map<string, { from: number; to: number }>();
    for (const s of displaySessions) {
      if (s.status !== 'blocked') continue;
      if ((s.duration ?? 60) < 1380) continue; // partial blocks render as SessionItem, not overlay
      const sStartMin = timeToMinutes(s.time);
      const sEndMin = sStartMin + (s.duration || 60);
      const startHour = Math.floor(sStartMin / 60);
      const endHour = Math.ceil(sEndMin / 60);
      for (let hour = startHour; hour < endHour; hour++) {
        const hourStr = String(hour).padStart(2, '0');
        const key = `${s.date}-${hourStr}`;
        const hourStart = hour * 60;
        const segStart = Math.max(sStartMin, hourStart);
        const segEnd = Math.min(sEndMin, hourStart + 60);
        const from = (segStart - hourStart) / 60;
        const to = (segEnd - hourStart) / 60;
        const existing = map.get(key);
        if (existing) {
          map.set(key, { from: Math.min(existing.from, from), to: Math.max(existing.to, to) });
        } else {
          map.set(key, { from, to });
        }
      }
    }
    return map;
  }, [displaySessions]);

  const dateSpecificRanges = useMemo(() => {
    const ranges: Array<{ date: string; startTime: string; endTime: string; weekdayIdx: number; scheduleType?: 'standard' | 'reschedule_only' }> = [];

    for (const slot of calData.dateSpecificSlots) {
      const [y, m, d] = slot.date.split('-').map(Number);
      const slotDate = new Date(y, m - 1, d);
      const jsDay = slotDate.getDay();
      const weekdayIdx = jsDay === 0 ? 6 : jsDay - 1;

      const slotStart = timeToMinutes(slot.startTime);
      let slotEnd = timeToMinutes(slot.endTime);
      if (slotEnd === 0) slotEnd = 24 * 60;

      const recurring: Array<[number, number]> = [];
      mappedTemplate.forEach(t => {
        if (!t.active || t.weekday !== weekdayIdx) return;
        const tStart = timeToMinutes(t.startTime);
        let tEnd = timeToMinutes(t.endTime);
        if (tEnd === 0) tEnd = 24 * 60;
        if (tStart < slotEnd && tEnd > slotStart) {
          recurring.push([Math.max(tStart, slotStart), Math.min(tEnd, slotEnd)]);
        }
      });

      if (recurring.length === 0) {
        ranges.push({ ...slot, weekdayIdx });
        continue;
      }

      recurring.sort((a, b) => a[0] - b[0]);
      const merged: Array<[number, number]> = [];
      for (const interval of recurring) {
        if (merged.length === 0 || merged[merged.length - 1][1] < interval[0]) {
          merged.push([...interval]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], interval[1]);
        }
      }

      let cursor = slotStart;
      let hasDiff = false;
      for (const [rStart, rEnd] of merged) {
        if (cursor < rStart) { hasDiff = true; break; }
        cursor = Math.max(cursor, rEnd);
      }
      if (!hasDiff && cursor < slotEnd) hasDiff = true;

      if (hasDiff) {
        ranges.push({ ...slot, weekdayIdx });
      }
    }

    ranges.sort((a, b) => {
      if (a.weekdayIdx !== b.weekdayIdx) return a.weekdayIdx - b.weekdayIdx;
      return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    });
    const result: typeof ranges = [];
    for (const r of ranges) {
      const last = result[result.length - 1];
      if (last && last.date === r.date && last.weekdayIdx === r.weekdayIdx && (last.scheduleType ?? 'standard') === (r.scheduleType ?? 'standard') && timeToMinutes(last.endTime) >= timeToMinutes(r.startTime)) {
        const rEnd = timeToMinutes(r.endTime);
        if (rEnd > timeToMinutes(last.endTime)) {
          last.endTime = r.endTime;
        }
      } else {
        result.push({ ...r });
      }
    }

    return result;
  }, [calData.dateSpecificSlots, mappedTemplate]);

  const dateSpecificRangeByCell = useMemo(() => {
    const map = new Map<string, { date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>();
    for (const range of dateSpecificRanges) {
      const startMin = timeToMinutes(range.startTime);
      let endMin = timeToMinutes(range.endTime);
      if (endMin === 0) endMin = 24 * 60;
      const firstHour = Math.floor(startMin / 60);
      const lastHour = Math.ceil(endMin / 60);
      for (let h = firstHour; h < lastHour; h++) {
        const key = `${range.weekdayIdx}-${String(h).padStart(2, '0')}`;
        if (!map.has(key)) map.set(key, { date: range.date, startTime: range.startTime, endTime: range.endTime, scheduleType: range.scheduleType });
      }
    }
    return map;
  }, [dateSpecificRanges]);

  // Resumen por semana visible de qué días ya tienen "Día específico" configurado — se muestra
  // en SpecificDayPanel antes de elegir fecha. Reusa dateSpecificRanges (ya agrupado por
  // weekdayIdx para el overlay del grid), agrupando además por fecha para juntar los rangos
  // de un mismo día en una sola entrada.
  const weekSummary = useMemo(() => {
    const byDate = new Map<string, { date: string; weekdayIdx: number; ranges: Array<{ startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }> }>();
    for (const range of dateSpecificRanges) {
      const existing = byDate.get(range.date);
      const entry = { startTime: range.startTime, endTime: range.endTime, scheduleType: range.scheduleType };
      if (existing) {
        existing.ranges.push(entry);
      } else {
        byDate.set(range.date, { date: range.date, weekdayIdx: range.weekdayIdx, ranges: [entry] });
      }
    }
    return Array.from(byDate.values()).sort((a, b) => a.weekdayIdx - b.weekdayIdx);
  }, [dateSpecificRanges]);

  const specificDayPreviewByCell = useMemo(() => {
    const map = new Map<string, { date: string; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>();

    for (const slot of calScheduleOps.specificDayPreview) {
      if (!slot.startTime || !slot.endTime) continue;
      const [y, m, d] = slot.date.split('-').map(Number);
      const slotDate = new Date(y, m - 1, d);
      const jsDayP = slotDate.getDay();
      const wIdx = jsDayP === 0 ? 6 : jsDayP - 1;

      const slotStart = timeToMinutes(slot.startTime);
      let slotEnd = timeToMinutes(slot.endTime);
      if (slotEnd === 0) slotEnd = 24 * 60;

      const recurring: Array<[number, number]> = [];
      mappedTemplate.forEach(t => {
        if (!t.active || t.weekday !== wIdx) return;
        const tStart = timeToMinutes(t.startTime);
        let tEnd = timeToMinutes(t.endTime);
        if (tEnd === 0) tEnd = 24 * 60;
        if (tStart < slotEnd && tEnd > slotStart) {
          recurring.push([Math.max(tStart, slotStart), Math.min(tEnd, slotEnd)]);
        }
      });

      let hasDiff = recurring.length === 0;
      if (!hasDiff) {
        recurring.sort((a, b) => a[0] - b[0]);
        const merged: Array<[number, number]> = [];
        for (const iv of recurring) {
          if (merged.length === 0 || merged[merged.length - 1][1] < iv[0]) {
            merged.push([iv[0], iv[1]]);
          } else {
            merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], iv[1]);
          }
        }
        let cursor = slotStart;
        for (const [rStart, rEnd] of merged) {
          if (cursor < rStart) { hasDiff = true; break; }
          cursor = Math.max(cursor, rEnd);
        }
        if (!hasDiff && cursor < slotEnd) hasDiff = true;
      }

      if (hasDiff) {
        const startMin = timeToMinutes(slot.startTime);
        let endMin = timeToMinutes(slot.endTime);
        if (endMin === 0) endMin = 24 * 60;
        for (let h = Math.floor(startMin / 60); h < Math.ceil(endMin / 60); h++) {
          const key = `${wIdx}-${String(h).padStart(2, '0')}`;
          if (!map.has(key)) map.set(key, { date: slot.date, startTime: slot.startTime, endTime: slot.endTime, scheduleType: slot.scheduleType });
        }
      }
    }
    return map;
  }, [calScheduleOps.specificDayPreview, mappedTemplate]);

  // -------------------------------------------------------------------------
  // Initial scroll on mount — position grid at current hour − 5
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isGridMounted || !gridRef.current) return;
    // Anchor the grid to the psychologist's local business hours (their configured TZ)
    const tzHourRaw = new Date().toLocaleTimeString('en-CA', {
      timeZone: selectedTimeZone, hour: '2-digit', hour12: false,
    });
    const tzHour = parseInt(tzHourRaw.slice(0, 2), 10);
    const hour = Number.isNaN(tzHour) ? 8 : tzHour;

    if (isMobile) {
      // On mobile, TimeGrid is full-height (no internal scroll) — scroll the page instead
      const gridTop = gridRef.current.getBoundingClientRect().top + window.scrollY;
      const startIdx = Math.min(Math.max(0, hour - 3), 21);
      window.scrollTo({ top: gridTop + startIdx * ROW_HEIGHT_PX });
    } else {
      const startIdx = Math.min(Math.max(0, hour - 5), 14);
      gridRef.current.scrollTop = startIdx * ROW_HEIGHT_PX;
    }
  }, [isGridMounted, isMobile, ROW_HEIGHT_PX, selectedTimeZone]);

  // -------------------------------------------------------------------------
  // Wheel handler — only blocks scroll during loading states
  // -------------------------------------------------------------------------

  useEffect(() => {
    const el = gridRef.current;
    if (!el || isMobile) return;

    const onWheelNative = (e: WheelEvent) => {
      if (!el.contains(e.target as Node)) return;
      if (isBusy || blockAll) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (tooltipTimerRef.current) {
        window.clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
      setTooltip(prev => prev === null ? prev : null);
      setHoveredCell(prev => prev === null ? prev : null);
    };

    window.addEventListener('wheel', onWheelNative, { passive: false });
    return () => window.removeEventListener('wheel', onWheelNative);
  }, [isBusy, blockAll, isMobile, isGridMounted]);

  // -------------------------------------------------------------------------
  // Onboarding (psi-* events) — stays in component
  // -------------------------------------------------------------------------

  useEffect(() => {
    const baseAppointmentTypes = appointmentTypes;

    const handleOpenWorkRange = () => {
      const date = availability[0]?.date ?? '';
      calScheduleOps.setTemplateModal({ date, startTime: '', endTime: '', weekdayIdx: 0 });
    };
    const handleCloseWorkRange = () => {
      calScheduleOps.setTemplateModal(null);
    };
    const handleShowMockBooking = (e?: Event) => {
      // Detail opcional (sólo usado por tests e2e) para abrir el modal en una fecha/hora
      // exacta y ejercer la revalidación de "Requiere coordinación" al confirmar — el
      // resto de los tests siguen usando el default (+3 días, sin coordinación).
      const detail = (e as CustomEvent<{ date?: string; startTime?: string; endTime?: string } | undefined>)?.detail;

      let dateStr = detail?.date;
      if (!dateStr) {
        const futureDate = new Date();
        const dayOfWeek = futureDate.getDay(); // 0=Dom, 6=Sáb
        // Si es sábado o domingo, calcular explícitamente el próximo miércoles (semana siguiente)
        // para garantizar >24h de anticipación y que la fecha esté en la semana siguiente.
        if (dayOfWeek === 6) {
          // Sábado → próximo lunes (+2) + 2 = miércoles (+4)
          futureDate.setDate(futureDate.getDate() + 4);
        } else if (dayOfWeek === 0) {
          // Domingo → próximo lunes (+1) + 2 = miércoles (+3)
          futureDate.setDate(futureDate.getDate() + 3);
        } else {
          futureDate.setDate(futureDate.getDate() + 3);
        }
        const yyyy = futureDate.getFullYear();
        const mm = String(futureDate.getMonth() + 1).padStart(2, '0');
        const dd = String(futureDate.getDate()).padStart(2, '0');
        dateStr = `${yyyy}-${mm}-${dd}`;
      }

      if (!calBooking.selectedPatientEmail) {
        calBooking.selectPatient('ana.lopez@demo.psi', 'mock-process-individual');
        const mockType = baseAppointmentTypes.find(t => t.name === 'Individual');
        if (mockType) {
          calBooking.setSelectedAppointmentTypeId(mockType.id);
          calBooking.setSelectedAppointmentType(mockType);
        }
      }
      calBooking.setDragModal({
        date: dateStr,
        startTime: detail?.startTime || '10:00',
        endTime: detail?.endTime || '11:00',
      });
    };
    const handleShowMockAvailability = () => {
      setMockAvailability(true);
      if (!calBooking.selectedPatientEmail || !calBooking.selectedPatientEmail.endsWith('@demo.psi')) {
        calBooking.selectPatient('ana.lopez@demo.psi', 'mock-process-individual');
        const mockType = baseAppointmentTypes.find(t => t.name === 'Individual');
        if (mockType) {
          calBooking.setSelectedAppointmentTypeId(mockType.id);
          calBooking.setSelectedAppointmentType(mockType);
        }
      }
      const activeSchedules = calData.template.filter(t => t.active);
      if (activeSchedules.length > 0) {
        const startTimes = activeSchedules.map(t => timeToMinutes(t.startTime));
        const minStart = Math.min(...startTimes);
        const targetIdx = Math.max(0, Math.floor(minStart / 60) - 1);
        if (gridRef.current) gridRef.current.scrollTop = targetIdx * ROW_HEIGHT_PX;
      }
    };
    const handleHideMockAvailability = () => { setMockAvailability(false); setMockBookingTourTemplate(null); };
    const handleShowMockReschedule = () => setMockRescheduleOverlay(true);
    const handleHideMockReschedule = () => setMockRescheduleOverlay(false);
    const handleShowMockRescheduleConfirm = () => setMockRescheduleConfirmVisible(true);
    const handleShowMockSessionDetail = () => {
      const tryOpen = () => {
        const nearSession = allSessionsRef.current.find(
          s => s.status !== 'blocked' && ['active', 'scheduled', 'settled', 'incomplete', 'free'].includes(s.status)
        );
        if (nearSession) {
          handleSessionClickRef.current(nearSession);
          return true;
        }
        return false;
      };
      // Retry in case React state updates haven't propagated yet
      if (!tryOpen()) {
        setTimeout(() => { if (!tryOpen()) setTimeout(() => tryOpen(), 500); }, 300);
      }
    };

    window.addEventListener('psi-open-create-work-range', handleOpenWorkRange);
    window.addEventListener('psi-close-work-range-and-exit', handleCloseWorkRange);
    window.addEventListener('psi-show-mock-booking-modal', handleShowMockBooking);
    window.addEventListener('psi-show-mock-availability', handleShowMockAvailability);
    window.addEventListener('psi-hide-mock-availability', handleHideMockAvailability);
    window.addEventListener('psi-show-mock-reschedule', handleShowMockReschedule);
    window.addEventListener('psi-hide-mock-reschedule', handleHideMockReschedule);
    window.addEventListener('psi-show-mock-reschedule-confirm', handleShowMockRescheduleConfirm);
    window.addEventListener('psi-show-mock-session-detail', handleShowMockSessionDetail);
    return () => {
      window.removeEventListener('psi-open-create-work-range', handleOpenWorkRange);
      window.removeEventListener('psi-close-work-range-and-exit', handleCloseWorkRange);
      window.removeEventListener('psi-show-mock-booking-modal', handleShowMockBooking);
      window.removeEventListener('psi-show-mock-availability', handleShowMockAvailability);
      window.removeEventListener('psi-hide-mock-availability', handleHideMockAvailability);
      window.removeEventListener('psi-show-mock-reschedule', handleShowMockReschedule);
      window.removeEventListener('psi-hide-mock-reschedule', handleHideMockReschedule);
      window.removeEventListener('psi-show-mock-reschedule-confirm', handleShowMockRescheduleConfirm);
      window.removeEventListener('psi-show-mock-session-detail', handleShowMockSessionDetail);
    };
  }, [availability, calData.template, calBooking.selectedPatientEmail, appointmentTypes]);

  // -------------------------------------------------------------------------
  // Shortcuts / aliases for cleaner JSX
  // -------------------------------------------------------------------------

  const {
    sessions: _sessions,
    setSessions,
    patients,
    template,
    loading,
    initialGridReady,
    refreshing,
    templateReady,
    panelPreviewSchedules,
    setPanelPreviewSchedules,
    deletedScheduleIds,
    setDeletedScheduleIds,
    dateSpecificSlots,
    loadData,
    fetchDateSpecificSlotsForWeek,
    sessionsCacheByWeekRef,
    pendingFetchesRef,
    availabilityCacheRef,
    dateSpecificSlotsCacheRef,
    availabilityPendingRef,
  } = calData;

  const {
    selectedPatientEmail,
    selectedProcessId,
    selectPatient,
    selectedAppointmentTypeId,
    setSelectedAppointmentTypeId,
    selectedAppointmentType,
    setSelectedAppointmentType,
    selectPatientAndTypeModal,
    setSelectPatientAndTypeModal,
    dragModal,
    setDragModal,
    setDragModalSafe,
    isBooking,
    patientsLoading,
    showDayLimitModal,
    setShowDayLimitModal,
    effectivePatients,
    selectedPatient,
    compatibleAppointmentTypes,
    handleBookingConfirm,
    patientTimezone,
  } = calBooking;

  const {
    templateModal,
    setTemplateModal,
    editModal,
    setEditModal,
    editSpecificDayModal,
    setEditSpecificDayModal,
    setSpecificDayPreview,
    specificDaySelectedDate,
    setSpecificDaySelectedDate,
    showLoadingModal,
    selectedRangeId,
    setSelectedRangeId,
    workScheduleSavedRef,
    specificDayHasChangesRef,
    blockHasChangesRef,
    openCreateWeekdayIdx,
    handleSetTemplateModal,
    handlePanelSave,
    handleSpecificDaySave,
    handleCreateWorkRangeModalSave,
    handleEditWorkRangeModalDelete,
    handleEditWorkRangeModalSave,
    handleEditSpecificDayModalDelete,
    handleEditSpecificDayModalSave,
    handleWorkRangeOverlayDelete,
  } = calScheduleOps;

  const {
    isRescheduling,
    reschedulingSession,
    setReschedulingSession,
    isPreparingReschedule,
    setIsPreparingReschedule,
    unblockConfirm,
    setUnblockConfirm,
    unblockingSessionId,
    handleRefreshScheduledSession,
    handleSessionClickMemo,
    handleSessionUnblockMemo,
    handleUnblockConfirmed,
    handleRescheduleSession,
  } = calReschedule;

  const baseAppointmentTypes = useMemo(() => appointmentTypes, [appointmentTypes]);

  // Refs for stable access inside event handlers (avoids stale closures)
  const allSessionsRef = useRef(allSessions);
  allSessionsRef.current = allSessions;
  const handleSessionClickRef = useRef(handleSessionClickMemo);
  handleSessionClickRef.current = handleSessionClickMemo;

  // Auto-open session modal when navigating from Patients tab
  const autoOpenConsumedRef = useRef(false);
  useEffect(() => {
    if (!autoOpenPatientEmail || !autoOpenDate || autoOpenConsumedRef.current) return;
    if (allSessions.length === 0) return;
    const targetDate = autoOpenDate.split("T")[0].split(" ")[0];
    const session = allSessions.find(
      s =>
        s.patient.email === autoOpenPatientEmail &&
        (s.date === targetDate || (s.origISO ?? '').startsWith(targetDate)) &&
        s.status !== 'blocked' &&
        ['active', 'scheduled', 'settled', 'incomplete', 'free'].includes(s.status)
    );
    if (!session) return;
    autoOpenConsumedRef.current = true;
    handleSessionClickRef.current(session);
    onAutoOpenConsumed?.();
  }, [allSessions, autoOpenPatientEmail, autoOpenDate, onAutoOpenConsumed]);

  // Deep-link compartible: abrir el modal de detalle de una sesión por id.
  // Si la sesión no está en la semana visible y viene autoOpenDate, navega primero a esa
  // semana y el effect reintenta cuando cargan sus sesiones.
  const [deepLinkSessionView, setDeepLinkSessionView] = useState<'details' | 'reschedule'>('details');
  const autoOpenSessionConsumedRef = useRef(false);
  useEffect(() => {
    if (!autoOpenSessionId || autoOpenSessionConsumedRef.current) return;
    if (autoOpenDate) {
      const targetDate = autoOpenDate.split('T')[0].split(' ')[0];
      const [y, m, d] = targetDate.split('-').map(Number);
      if (y && m && d) {
        const dateObj = new Date(y, m - 1, d);
        const day = dateObj.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;
        const ws = new Date(dateObj);
        ws.setDate(dateObj.getDate() + mondayOffset);
        ws.setHours(0, 0, 0, 0);
        if (ws.getTime() !== startOfWeek.getTime()) {
          navigation.setStartOfWeek(ws);
          return;
        }
      }
    }
    if (allSessions.length === 0) return;
    const session = allSessions.find((s) => s.id === autoOpenSessionId);
    if (!session) return;
    autoOpenSessionConsumedRef.current = true;
    setDeepLinkSessionView(autoOpenSessionView === 'reschedule' ? 'reschedule' : 'details');
    handleSessionClickRef.current(session);
    onAutoOpenSessionConsumed?.();
  }, [allSessions, autoOpenSessionId, autoOpenSessionView, autoOpenDate, startOfWeek, navigation, onAutoOpenSessionConsumed]);

  // Clear reschedule mode when the banner's Cancel button fires
  const lastCancelTickRef = useRef(0);
  useEffect(() => {
    if (!cancelSchedulingTick || cancelSchedulingTick === lastCancelTickRef.current) return;
    lastCancelTickRef.current = cancelSchedulingTick;
    setReschedulingSession(null);
    setIsPreparingReschedule(false);
  }, [cancelSchedulingTick, setReschedulingSession, setIsPreparingReschedule]);

  // -------------------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------------------

  return (
    <div className="calendar-section" style={{ userSelect: 'none', position: 'relative', overflow: 'visible' }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .calendar-section { direction: ltr; }
        .calendar-header { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; padding: 0.5rem 0; gap: 0.75rem; }
        .header-left { display: flex; align-items: center; gap: 0.5rem; justify-self: start; min-width: 0; }
        .header-center { min-width: 0; display: flex; justify-content: center; justify-self: center; }
        .header-right { display: flex; align-items: center; justify-self: end; min-width: 0; }
        .calendar-header h3 { margin: 0; font-size: 1.25rem !important; font-weight: 700; text-align: center !important; white-space: nowrap; }
        .btn-today { padding: 0.5rem 1rem; font-size: 0.875rem !important; background: #fff; border: 1px solid #cbd5e1; border-radius: 4px; cursor: pointer; }
        .chevrons { display: flex; align-items: center; gap: 0.5rem; margin-left: 0.25rem; }
        .chevron-btn { width: 2rem; height: 2rem; font-size: 1.125rem !important; border: 1px solid #cbd5e1; background: #fff; color: #111827; display: grid; place-items: center; cursor: pointer; }
        .chevron-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .loading-schedule { padding: 0.75rem; font-size: 0.875rem !important; text-align: left; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; }
        .modal-content { background: #fff; border-radius: 0.5rem; width: 28rem; max-width: 90vw; }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.75rem 1rem; border-bottom: 1px solid #e5e7eb; }
        .modal-header h3 { margin: 0; font-size: 1rem !important; text-align: left !important; }
        .btn-close { background: transparent; border: 0; font-size: 1.25rem !important; cursor: pointer; }
        .modal-body { padding: 0.75rem 1rem; font-size: 0.875rem !important; text-align: left; }
        .modal-body label { display: block; margin-bottom: 0.25rem; font-size: 0.875rem !important; }
        .modal-body select { width: 100%; padding: 0.5rem; font-size: 0.875rem !important; }
        .modal-footer { padding: 0.75rem 1rem; display: flex; justify-content: flex-end; gap: 0.5rem; border-top: 1px solid #e5e7eb; }
        .btn-secondary { padding: 0.5rem 0.75rem; font-size: 0.875rem !important; cursor: pointer; }
        .btn-primary { padding: 0.5rem 0.75rem; font-size: 0.875rem !important; cursor: pointer; background: #6D57BC; color: #fff; border: 1px solid #6D57BC; border-radius: 9999px; }
        .cell-tooltip { position: fixed; background: #fff; color: #111827; padding: 1rem; border-radius: 0.5rem; font-size: 0.875rem !important; line-height: 1.5; max-width: 18rem; z-index: 9999; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05); pointer-events: none; animation: fadeIn 0.2s ease-out; border: 1px solid #e5e7eb; }
        .cell-tooltip-label-edit { font-weight: 700; margin-bottom: 0.5rem; color: #6D57BC; font-size: 0.875rem !important; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
        .cell-tooltip-label-schedule { font-weight: 700; margin-bottom: 0.5rem; color: #6D57BC; font-size: 0.875rem !important; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
        .cell-tooltip-text { margin-bottom: 0.375rem; color: #374151; font-size: 0.8125rem !important; }
        .cell-tooltip-text:last-child { margin-bottom: 0; }
        .time-header-highlight { background: rgba(109, 40, 217, 0.08) !important; }
        .day-header-highlight { background: rgba(109, 40, 217, 0.08) !important; }
        .time-header-highlight-schedule { background: rgba(109, 40, 217, 0.08) !important; }
        .day-header-highlight-schedule { background: rgb(186, 185, 187)) !important; }
      `}</style>
      {dragErrorMessage && (
        <div
          className="drag-error-indicator"
          style={{
            position: 'fixed',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(245, 158, 11, 0.95)',
            color: '#fff',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '0.875rem !important',
            fontWeight: 700,
            zIndex: 10001,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          ⚠️ {dragErrorMessage}
        </div>
      )}
      {tooltip && (() => {
        const TOOLTIP_W = 288; // max-width: 18rem
        const TOOLTIP_H = 160; // estimated max height
        const GAP = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // Horizontal: prefer right of slot, flip left if not enough room
        const placeRight = tooltip.rect.right + GAP + TOOLTIP_W <= vw;
        const left = placeRight
          ? tooltip.rect.right + GAP
          : tooltip.rect.left - TOOLTIP_W - GAP;
        // Vertical: align top of popup with top of slot, clamp to viewport
        const rawTop = tooltip.rect.top;
        const top = Math.min(rawTop, vh - TOOLTIP_H - GAP);
        return (
        <div
          className="cell-tooltip"
          style={{ left, top }}
        >
          <div
            data-testid="cell-tooltip-label"
            className={editMode ? "cell-tooltip-label-edit" : "cell-tooltip-label-schedule"}
          >
            {tooltip.time}{cellTooltipIso2 && <span aria-hidden="true"> <CountryFlag iso2={cellTooltipIso2} size="0.85rem" /></span>} - {tooltip.date}
          </div>
          {tooltip.content.split('\n').map((line, i) => (
            <div key={i} data-testid="cell-tooltip-text" className="cell-tooltip-text">{line}</div>
          ))}
        </div>
        );
      })()}
      <CalendarHeader
        startOfWeek={startOfWeek}
        canGoPrev={isMobile ? mobileCanGoPrev : canGoPrev}
        canGoNext={isMobile ? mobileCanGoNext : canGoNext}
        onPrev={isMobile ? onMobilePrev : () => moveWeek(-1)}
        onNext={isMobile ? onMobileNext : () => moveWeek(1)}
        onToday={() => {
          goToToday();
          const dow = new Date().getDay();
          const dayIdx = dow === 0 ? 6 : dow - 1;
          setMobileDayIndex(dayIdx < 3 ? 0 : 3);
        }}
        timezone={selectedTimeZone}
        editMode={editMode}
        showDate={openCreateModalForDate !== null && openCreateModalForDate !== undefined}
        viewSelector={viewSelector}
      />

      {loading || !initialGridReady || refreshing ? (
        <div className="overflow-hidden rounded-xl border border-gray-100 mx-1">
          {/* Day column headers */}
          <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: '3.5rem repeat(7, 1fr)' }}>
            <div className="h-12 border-r border-gray-100" />
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-12 flex flex-col items-center justify-center gap-1 border-r border-gray-100 last:border-r-0">
                <div className="h-2.5 w-5 skeleton-shimmer rounded" />
                <div className="h-4 w-7 skeleton-shimmer rounded" />
              </div>
            ))}
          </div>
          {/* Time rows */}
          {[...Array(10)].map((_, row) => (
            <div key={row} className="grid border-b border-gray-100 last:border-b-0 relative" style={{ gridTemplateColumns: '3.5rem repeat(7, 1fr)', height: '3.5rem' }}>
              <div className="flex items-start justify-end pr-2 pt-1 border-r border-gray-100">
                <div className="h-2.5 w-8 skeleton-shimmer rounded" />
              </div>
              {[...Array(7)].map((_, col) => (
                <div key={col} className="border-r border-gray-100 last:border-r-0 relative">
                  {((row === 1 && col === 2) || (row === 4 && col === 0) || (row === 6 && col === 4) || (row === 2 && col === 6)) && (
                    <div className="absolute inset-1 skeleton-shimmer rounded-md opacity-40" style={{ background: 'linear-gradient(90deg, #ddd6fe 25%, #ede9fe 50%, #ddd6fe 75%)', backgroundSize: '400% 100%', animation: 'skeleton-shimmer 1.6s ease-in-out infinite' }} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : timeSlots.length === 0 ? (
        <div className="loading-schedule">
          <small></small>
        </div>
      ) : templateReady && template.length === 0 && !editMode && !(isTourRunning && !!mockBookingTourTemplate && !selectPatientAndTypeModal) && localStorage.getItem('psi-calendar-tour-done') !== '1' ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem 2rem',
          background: '#f9fafb',
          borderRadius: '0.5rem',
          border: '2px dashed #cbd5e1'
        }}>
          <div style={{
            fontSize: '3rem !important',
            marginBottom: '1rem',
            opacity: 0.5
          }}>
            📅
          </div>
          <h3 style={{
            fontSize: '1.5rem !important',
            fontWeight: 700,
            color: '#374151',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            Comenzar
          </h3>
          <p style={{
            fontSize: '1rem !important',
            color: '#6b7280',
            textAlign: 'center',
            maxWidth: '28rem',
            marginBottom: '1.5rem'
          }}>
            Configura tus rangos de trabajo disponibles para comenzar a usar la agenda.
          </p>
          {onEditModeToggle && (
            <button
              onClick={onEditModeToggle}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem !important',
                fontWeight: 700,
                background: '#6D57BC',
                color: '#fff',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = '#5b21b6';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = '#6D57BC';
              }}
            >
              Configurar Horarios
            </button>
          )}
        </div>
      ) : (
      <div style={{ display: 'flex', gap: '1rem', overflow: 'visible', position: 'relative' }}>
        {editMode && openCreateModalForDate === null && !openBlockModalTick && (
          <WorkSchedulePanel
            existingSchedules={isOnboardingMockMode ? [] : template.filter(t => t.active)}
            defaultScheduleType={defaultPanelScheduleType}
            onSave={async (actions) => {
              setDefaultPanelScheduleType('standard');
              try {
                if ((window as unknown as { __psiOnboardingMockCreate?: boolean }).__psiOnboardingMockCreate === true) {
                  showSuccess('Horario creado exitosamente (demo)');
                  return;
                }
              } catch { /* noop */ }
              await handlePanelSave(actions);
              workScheduleSavedRef.current = true;
            }}
            onCancel={() => {
              setDefaultPanelScheduleType('standard');
              try {
                if ((window as unknown as { __psiOnboardingMockCreate?: boolean }).__psiOnboardingMockCreate === true) {
                  if (panelPreviewSchedules && panelPreviewSchedules.length > 0) {
                    setMockBookingTourTemplate(panelPreviewSchedules);
                  }
                  window.dispatchEvent(new CustomEvent('psi-work-range-created'));
                  return;
                }
              } catch { /* noop */ }
              const wasSaved = workScheduleSavedRef.current;
              workScheduleSavedRef.current = false;
              window.dispatchEvent(new CustomEvent('psi-work-schedule-cancel', { detail: { wasSaved } }));
            }}
            loading={showLoadingModal}
            onHasChangesChange={onScheduleHasChangesChange}
            onPreviewChange={setPanelPreviewSchedules}
            onDeletedSchedulesChange={setDeletedScheduleIds}
            mockRescheduleEntry={mockRescheduleOverlay}
            onOpenAgendaHelp={onOpenAgendaHelp}
          />
        )}
        {editMode && openCreateModalForDate !== null && !openBlockModalTick && (
          <SpecificDayPanel
            initialDate={openCreateModalForDate ?? ''}
            existingSchedules={template.filter(t => t.active)}
            dateSpecificSlots={dateSpecificSlots}
            weekSummary={weekSummary}
            onSave={handleSpecificDaySave}
            onClose={() => {
              if (specificDayHasChangesRef.current) {
                window.dispatchEvent(new CustomEvent('psi-specific-day-cancel'));
              } else {
                setSpecificDayPreview([]);
                onCloseCreateModal?.();
              }
            }}
            onPreviewChange={setSpecificDayPreview}
            fetchSlotsForWeek={fetchDateSpecificSlotsForWeek}
            onDateChange={(date) => {
              const [y, m, d] = date.split('-').map(Number);
              const dateObj = new Date(y, (m || 1) - 1, d || 1);
              const day = dateObj.getDay();
              const mondayOffset = day === 0 ? -6 : 1 - day;
              const weekStart = new Date(dateObj);
              weekStart.setDate(dateObj.getDate() + mondayOffset);
              weekStart.setHours(0, 0, 0, 0);
              if (weekStart.getTime() !== startOfWeek.getTime()) {
                navigation.setStartOfWeek(weekStart);
              }
              setSpecificDaySelectedDate(date);
            }}
            loading={showLoadingModal}
            onHasChangesChange={(v) => {
              specificDayHasChangesRef.current = v;
              onSpecificDayHasChangesChange?.(v);
            }}
            blockedSlots={_sessions.filter((s: { status: string }) => s.status === 'blocked')}
            mockRescheduleEntry={mockRescheduleOverlay}
            onOpenAgendaHelp={onOpenAgendaHelp}
          />
        )}
        {editMode && openBlockModalTick && (
          <BlockSchedulePanel
            psychologistId={psychologistId || ''}
            sessions={calData.sessions}
            setSessions={setSessions}
            onClose={() => {
              if (blockHasChangesRef.current) {
                window.dispatchEvent(new CustomEvent('psi-block-cancel'));
              } else {
                onCloseBlockModal();
              }
            }}
            timezone={selectedTimeZone}
            onHasChangesChange={(v) => {
              blockHasChangesRef.current = v;
              onBlockHasChangesChange?.(v);
            }}
            onSuccess={() => {
              const endOfWeek = new Date(startOfWeek);
              endOfWeek.setDate(startOfWeek.getDate() + 6);
              const wk = `${startOfWeek.toISOString().slice(0,10)}_${endOfWeek.toISOString().slice(0,10)}_${psychologist.id}`;
              sessionsCacheByWeekRef.current.delete(wk);
              pendingFetchesRef.current.delete(`sessions:${wk}`);
              invalidateSessionsCache(wk);
              if (psychologistId) {
                invalidateSchedulesCache(String(psychologistId));
              }
              availabilityCacheRef.current.delete(wk);
              dateSpecificSlotsCacheRef.current.delete(wk);
              availabilityPendingRef.current.delete(wk);
              loadData();
              onCloseBlockModal();
            }}
            onOpenAgendaHelp={onOpenAgendaHelp}
          />
        )}
        <div
          id="calendar-interactive-grid"
          style={{ flex: 1, minWidth: 0, overflowX: 'visible' }}
          onMouseUpCapture={(e) => {
            // La selección de rango es vertical dentro de un solo día. Si el
            // mouseup ocurre sobre una celda de otra columna, cancelamos y
            // frenamos la propagación para que no se cree el rango ahí. Usamos la
            // columna real (data-day-index), no coordenadas calculadas.
            if (!dragging || dragging.weekdayIdx === undefined) return;
            const cellUnder = (e.target as HTMLElement)?.closest?.('[data-day-index]') as HTMLElement | null;
            if (!cellUnder) return;
            const colUnder = Number(cellUnder.getAttribute('data-day-index'));
            if (Number.isFinite(colUnder) && colUnder !== dragging.weekdayIdx) {
              e.stopPropagation();
              resetDrag();
            }
          }}
          onTouchStart={isMobile ? (e) => {
            const t = e.touches[0];
            swipeTouchStartRef.current = { x: t.clientX, y: t.clientY };
          } : undefined}
          onTouchEnd={isMobile ? (e) => {
            if (!swipeTouchStartRef.current) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - swipeTouchStartRef.current.x;
            const dy = t.clientY - swipeTouchStartRef.current.y;
            swipeTouchStartRef.current = null;
            // Only trigger if predominantly horizontal and >50px
            if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
            if (dx < 0) onMobileNext();
            else onMobilePrev();
          } : undefined}
        >
        <TimeGrid
          ref={gridRef}
          key={gridKey}
          onMount={() => setIsGridMounted(true)}
          onUnmount={() => setIsGridMounted(false)}
          availability={mobileAvailability}
          visibleTimeSlots={displayedTimeSlots}
          isSmallScreen={size === 's'}
          timezone={selectedTimeZone}
          onMouseMove={(e) => {
            if (!gridRef.current) return;
            const rect = gridRef.current.getBoundingClientRect();
            // El grid tiene scroll interno y los overlays (línea guía, selección)
            // se desplazan con el contenido. La posición del cursor es relativa al
            // viewport, así que hay que sumar scrollTop para anclarla al contenido.
            const scrollTop = gridRef.current.scrollTop || 0;
            const firstRowEl = gridRef.current.querySelector('[data-first-time-row="true"]') as HTMLDivElement | null;
            const headerHeight = firstRowEl ? (firstRowEl.getBoundingClientRect().top - rect.top) : 0;
            let y = e.clientY - rect.top;
            y = Math.max(0, Math.min(y, rect.height));

            mouseYRef.current = y;
            if (mouseLineRef.current) {
              mouseLineRef.current.style.top = (y + scrollTop) + 'px';
              mouseLineRef.current.style.display = '';
            }

            if (!dragging) return;

            // La selección de rango es vertical dentro de un solo día: si el
            // puntero se mueve a otra columna, se cancela el drag. Comparamos la
            // columna real de la celda bajo el cursor (data-day-index) con la de
            // origen, en vez de coordenadas calculadas (que arrastran offsets).
            const cellUnder = (e.target as HTMLElement)?.closest?.('[data-day-index]') as HTMLElement | null;
            if (cellUnder && dragging.weekdayIdx !== undefined) {
              const colUnder = Number(cellUnder.getAttribute('data-day-index'));
              if (Number.isFinite(colUnder) && colUnder !== dragging.weekdayIdx) {
                resetDrag();
                return;
              }
            }

            let yAdj = y - headerHeight;
            yAdj = Math.max(0, Math.min(yAdj, rect.height - headerHeight));
            const rowIdx = Math.floor(yAdj / ROW_HEIGHT_PX);
            const posInRow = yAdj - rowIdx * ROW_HEIGHT_PX;
            const quarterIndex = Math.floor(posInRow / QUARTER_HEIGHT_PX);
            const snappedTop = headerHeight + rowIdx * ROW_HEIGHT_PX + quarterIndex * QUARTER_HEIGHT_PX + scrollTop;
            const base = displayedTimeSlots[Math.max(0, Math.min(displayedTimeSlots.length - 1, rowIdx))] || dragging.startTime;
            handleMouseMove({ snappedTop, baseTimeSlot: base, quarterIndex, headerHeight, visibleTimeSlots: displayedTimeSlots });
            if (!editMode) {
              const tzNow = getNowInSelectedTZParts(selectedTimeZone);
              const todayStr = tzNow.todayStr;
              const baseHourStr = base.split(':')[0];
              const quarterMinutes = ['00', '30'][quarterIndex] || '00';
              const adjustedStart = `${baseHourStr}:${quarterMinutes}`;
              const tryStart = timeToMinutes(adjustedStart);
              const tryEnd = tryStart + 60;
              const isInTemplate = isLocalRangeWithinTemplate(mappedTemplate, startOfWeek, dragging?.date || todayStr, adjustedStart, 60);
              if (!isInTemplate) {
                setDragVisual({ startY: snappedTop, currentY: snappedTop + ROW_HEIGHT_PX });
                setDragBlocked(true);
                return;
              }
              const hasOverlap = hasOverlapAt(dragging?.date || todayStr, tryStart, tryEnd);
              const [dY, dM, dD] = (dragging?.date || todayStr).split('-').map(Number);
              const dragDateObj = new Date(dY, dM - 1, dD);
              dragDateObj.setHours(0, 0, 0, 0);
              const today0 = new Date(now);
              today0.setHours(0, 0, 0, 0);
              const isPastDay = dragDateObj < today0;
              const tzNowMinutes = timeToMinutes(`${tzNow.hour}:${toTwo(tzNow.minute)}`);
              const impersonating = isImpersonated();
              const blockedByPastDay = isPastDay && !impersonating;
              const blockedByPastTime = dragging?.date === todayStr && tryStart < tzNowMinutes && !impersonating;
              if (hasOverlap || blockedByPastTime || blockedByPastDay) {
                setDragVisual({ startY: snappedTop, currentY: snappedTop + ROW_HEIGHT_PX });
                setDragBlocked(true);
                return;
              }
              setDragVisual({ startY: snappedTop, currentY: snappedTop + ROW_HEIGHT_PX });
              setDragging(prev => prev ? { ...prev, startTime: adjustedStart, endTime: addMinutesToTime(adjustedStart, 60) } : prev);
              setDragBlocked(false);
            }
          }}
          onMouseLeave={() => {
            mouseYRef.current = null;
            if (mouseLineRef.current) mouseLineRef.current.style.display = 'none';
            if (dragVisual || dragging) resetDrag();
            if (tooltipTimerRef.current) {
              window.clearTimeout(tooltipTimerRef.current);
              tooltipTimerRef.current = null;
            }
            setTooltipMemo(null);
            setHoveredCellMemo(null);
          }}
          renderDayHeader={(day) => (
            <DayHeader
              key={`head-${day.date}`}
              dateString={day.date}
              highlighted={hoveredCell?.date === day.date || specificDaySelectedDate === day.date}
              editMode={editMode}
              showDate={openCreateModalForDate !== null && openCreateModalForDate !== undefined}
              isSmallScreen={size === 's'}
              isBlocked={isDayBlocked(day.date)}
              blockadeReason={getBlockadeReasonForDate(day.date)}
            />
          )}
          renderAllDayEvent={hasAnyBlockade ? (day) => {
            const reason = getBlockadeReasonForDate(day.date);
            if (!reason) return null;
            return (
              <div style={{
                margin: '2px 4px',
                padding: '2px 8px',
                background: '#6D57BC',
                color: '#fff',
                borderRadius: 6,
                fontSize: '0.7rem',
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
              }} title={reason}>
                {reason}
              </div>
            );
          } : undefined}
          renderTimeHeader={(ts, rowIdx) => (
            <TimeColumn
              label={ts}
              rowIdx={rowIdx}
              highlighted={hoveredCell?.time === ts}
              editMode={editMode}
              isSmallScreen={isMobile}
              onMouseEnter={() => undefined}
              onMouseLeave={() => undefined}
            />
          )}
          renderCell={(day, idx, ts, rowIdx) => {
            // On desktop idx === absolute weekday (all 7 days in availability array).
            // On mobile only a 3-4 day slice is passed, so idx is relative (0,1,2,3).
            // scheduleCellsMap / dateSpecificRangeByCell use the absolute weekday as key,
            // so we derive it from day.date to avoid a 2–3 hour offset on mobile.
            const jsDay = new Date(day.date + 'T12:00:00').getDay();
            const weekdayAbsIdx = jsDay === 0 ? 6 : jsDay - 1;
            const schedKey = (h: string) => `${weekdayAbsIdx}-${h}`;
            return (
            <CalendarCell
              day={day}
              idx={idx}
              ts={ts}
              rowIdx={rowIdx}
              template={isOnboardingMockMode ? [] : mappedTemplate}
              startOfWeek={startOfWeek}
              endOfWeek={endOfWeek}
              sessionsInSlot={getSessionsInSlot(day.date, ts.slice(0, 2))}
              hasSession={getSessionsInSlot(day.date, ts.slice(0, 2)).length > 0}
              hasOverlapAt={hasOverlapAt}
              now={now}
              selectedTimeZone={selectedTimeZone}
              editMode={editMode}
              isBusy={isBusy}
              blockAll={blockAll}
              hasScheduleCell={!!(scheduleCellsMap.get(schedKey(ts.slice(0, 2)))?.length)}
              scheduleCoverage={scheduleCellsMap.get(schedKey(ts.slice(0, 2))) || null}
              blockScheduling={blockScheduling}
              disableBooking={disableBooking}
              isDayBlocked={isDayBlocked(day.date)}
              dragging={dragging}
              dragVisual={dragVisual}
              dragBlocked={dragBlocked}
              visibleTimeSlots={displayedTimeSlots}
              ROW_HEIGHT_PX={ROW_HEIGHT_PX}
              QUARTER_HEIGHT_PX={QUARTER_HEIGHT_PX}
              patients={patients}
              psychologist={psychologistId}
              tooltipTimerRef={tooltipTimerRef}
              gridRef={gridRef}
              availability={availability}
              setHoveredCell={setHoveredCellMemo}
              setTooltip={setTooltipMemo}
              setSelectedRangeId={setSelectedRangeId}
              setTemplateModal={handleSetTemplateModal}
              setDragModal={setDragModalSafe}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              handleMouseDown={openBlockModalTick || openCreateModalForDate !== null ? () => { /* noop: disable drag in block/specific-day mode */ } : handleMouseDown}
              handleCellMouseUp={handleCellMouseUp}
              resetDrag={resetDrag}
              handleSessionClick={handleSessionClickMemo}
              handleSessionUnblock={handleSessionUnblockMemo}
              isSchedulingMode={isSchedulingMode || isRescheduleMode}
              availableSlots={availableSlotsByCell.get(`${day.date}-${ts}`) || null}
              selectedPatient={isRescheduleMode ? (reschedulingSession?.patient ?? null) : (selectedPatient || null)}
              reschedulingSessionId={reschedulingSession?.id ?? null}
              deletedScheduleIds={openBlockModalTick || openCreateModalForDate !== null ? new Set() : deletedScheduleIds}
              dateSpecificRange={
                openCreateModalForDate !== null
                  ? (weekdayAbsIdx === openCreateWeekdayIdx
                      ? (specificDayPreviewByCell.get(schedKey(ts.slice(0, 2))) || null)
                      : (dateSpecificRangeByCell.get(schedKey(ts.slice(0, 2))) || null))
                  : (dateSpecificRangeByCell.get(schedKey(ts.slice(0, 2))) || null)
              }
              onDateSpecificClick={editMode && openCreateModalForDate !== null ? (range) => setEditSpecificDayModal(range) : undefined}
              blockedCoverage={blockedCellsMap.get(`${day.date}-${ts.slice(0, 2)}`) || null}
              blockedSessionInSlot={getBlockedSessionInSlot(day.date, ts.slice(0, 2))}
              unblockingSessionId={unblockingSessionId}
            />
            );
          }}
          insideGridChildren={(
            <>
              <DragSelection
                dragVisual={dragVisual}
                selectionMask={selectionMask}
                dragBlocked={dragBlocked || false}
                editMode={editMode}
                startTime={dragging?.startTime}
                endTime={dragging?.endTime}
              />
              {editMode && !isBusy && (
                <WorkRangeOverlay
                  template={panelPreviewSchedules
                    ? panelPreviewSchedules.map(s => ({ ...s, psychologistId: psychologist.id }))
                    : isOnboardingMockMode ? [] : template}
                  visibleTimeSlots={displayedTimeSlots}
                  gridRef={gridRef}
                  selectedRangeId={selectedRangeId}
                  onSelectedChange={setSelectedRangeId}
                  onEdit={openBlockModalTick || openCreateModalForDate !== null ? undefined : (schedule) => setEditModal(schedule)}
                  isSmallScreen={size === 's'}
                  deletedScheduleIds={openBlockModalTick || openCreateModalForDate !== null ? new Set() : deletedScheduleIds}
                  onRestoreSchedule={openBlockModalTick || openCreateModalForDate !== null ? undefined : (scheduleId) => {
                    window.dispatchEvent(new CustomEvent('psi-restore-work-schedule', {
                      detail: { scheduleId }
                    }));
                  }}
                  onDelete={async (scheduleId) => {
                    await handleWorkRangeOverlayDelete(scheduleId);
                  }}
                />
              )}
              {((!editMode && !isBusy && !blockScheduling) || mockAvailability) && isGridMounted && gridRef.current && (
                <AvailableRangeOverlay
                  template={isTourRunning && mockBookingTourTemplate && !selectPatientAndTypeModal ? mockBookingTourTemplate.map(s => ({ ...s, psychologistId: psychologist.id })) : template}
                  sessions={displaySessions}
                  psychologistId={psychologist.id}
                  visibleTimeSlots={displayedTimeSlots}
                  gridRef={gridRef}
                  startOfWeek={startOfWeek}
                  selectedTimeZone={selectedTimeZone}
                  now={mockAvailability ? new Date(0) : now}
                  slotDuration={slotDuration}
                  isSchedulingMode={isSchedulingMode || isRescheduleMode}
                />
              )}
              {size !== 's' && (
                <div
                  ref={mouseLineRef}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: 1,
                    borderTop: editMode ? '1px dashed rgba(109, 40, 217, 0.3)' : '1px dashed rgba(16, 185, 129, 0.3)',
                    pointerEvents: 'none',
                    zIndex: 5,
                    display: 'none'
                  }}
                />
              )}
            </>
          )}
        />
      </div>
      </div>
      )}

      {selectPatientAndTypeModal && (
        <SelectPatientAndTypeModal
          open={true}
          onClose={() => {
            setSelectPatientAndTypeModal(null);
            selectPatient('');
            setSelectedAppointmentTypeId('');
            setSelectedAppointmentType(null);
            if (onPreloadConsumed) onPreloadConsumed();
          }}
          patients={effectivePatients}
          appointmentTypes={selectedPatient ? compatibleAppointmentTypes : []}
          selectedPatientEmail={selectedPatientEmail}
          selectedProcessId={selectedProcessId}
          selectedAppointmentTypeId={selectedAppointmentTypeId}
          onChangePatient={(email, processId) => selectPatient(email, processId)}
          onChangeAppointmentType={(typeId) => {
            setSelectedAppointmentTypeId(typeId);
            const type = baseAppointmentTypes.find(t => t.id === typeId) || null;
            setSelectedAppointmentType(type);
          }}
          mode={selectPatientAndTypeModal.mode}

          patientsLoading={patientsLoading}
          onConfirm={() => {
            setSelectPatientAndTypeModal(null);
            if (onPreloadConsumed) onPreloadConsumed();
            try { window.dispatchEvent(new CustomEvent('psi-booking-patient-confirmed')); } catch { /* noop */ }
          }}
        />
      )}
      {dragModal && isRescheduleMode && reschedulingSession && (() => {
        const rescheduleEndTime = dragModal.endTime || addMinutesToTime(dragModal.startTime, reschedulingSession.duration || 60);
        const currentDuration = reschedulingSession.duration || 60;
        const currentEndTime = addMinutesToTime(reschedulingSession.time, currentDuration);
        const rscPatient = reschedulingSession.patient;
        const rscPatientData = patients.find(p => p.process === reschedulingSession.process_id) || patients.find(p => p.email === rscPatient.email);
        const rscPatientTimezone = rscPatientData?.timezone || rscPatient.timezone || null;
        let rscPatientCurrentTime: string | null = null;
        let rscPatientCurrentEndTime: string | null = null;
        let rscPatientNewTime: string | null = null;
        let rscPatientNewEndTime: string | null = null;
        let rscPatientCountryCode: string | null = null;
        if (rscPatientTimezone) {
          try {
            const utcCurrentStart = localTzToUTC(reschedulingSession.date, reschedulingSession.time, selectedTimeZone);
            const utcCurrentEnd = new Date(utcCurrentStart.getTime() + currentDuration * 60000);
            rscPatientCurrentTime = isoToZoneParts(utcCurrentStart.toISOString(), rscPatientTimezone).time;
            rscPatientCurrentEndTime = isoToZoneParts(utcCurrentEnd.toISOString(), rscPatientTimezone).time;
          } catch { /* noop */ }
          try {
            const utcStart = localTzToUTC(dragModal.date, dragModal.startTime, selectedTimeZone);
            const utcEnd = new Date(utcStart.getTime() + currentDuration * 60000);
            rscPatientNewTime = isoToZoneParts(utcStart.toISOString(), rscPatientTimezone).time;
            rscPatientNewEndTime = isoToZoneParts(utcEnd.toISOString(), rscPatientTimezone).time;
          } catch { /* noop */ }
          rscPatientCountryCode = timezoneToCountryCode(rscPatientTimezone) || null;
        }
        return (
          <>
            <RescheduleConfirmModal
              open={!rescheduleReasonOpen}
              onClose={() => { setDragModal(null); setRescheduleReasonOpen(false); }}
              onConfirm={() => setRescheduleReasonOpen(true)}
              confirming={isRescheduling}
              sessionInfo={{
                patient: rscPatient.name || rscPatient.email,
                currentDate: reschedulingSession.date,
                currentTime: reschedulingSession.time,
                currentEndTime,
                duration: currentDuration,
                therapyType: reschedulingSession.specialty,
              }}
              newDate={dragModal.date}
              newTime={dragModal.startTime}
              newEndTime={rescheduleEndTime}
              therapistTimezone={selectedTimeZone}
              patientTimezone={rscPatientTimezone}
              patientCurrentTime={rscPatientCurrentTime}
              patientCurrentEndTime={rscPatientCurrentEndTime}
              patientNewTime={rscPatientNewTime}
              patientNewEndTime={rscPatientNewEndTime}
              patientCountryCode={rscPatientCountryCode}
            />
            <RescheduleReasonModal
              isOpen={rescheduleReasonOpen}
              confirming={isRescheduling}
              patientName={rscPatient.name || rscPatient.email}
              onClose={() => setRescheduleReasonOpen(false)}
              onConfirm={async (motiveId, note, initiatedBy) => {
                try {
                  await handleRescheduleSession(
                    reschedulingSession.id,
                    dragModal.date,
                    dragModal.startTime,
                    reschedulingSession.process_id,
                    { motiveId, note, initiatedBy }
                  );
                  setRescheduleReasonOpen(false);
                  setDragModal(null);
                  setReschedulingSession(null);
                } catch {
                  // Error handled inside handleRescheduleSession
                }
              }}
            />
          </>
        );
      })()}
      {dragModal && !isRescheduleMode && (
        <BookSessionModal
          open={true}
          onClose={() => setDragModal(null)}
          date={dragModal.date}
          startTime={dragModal.startTime}
          endTime={dragModal.endTime || addMinutesToTime(dragModal.startTime, selectedAppointmentType?.duration || 60)}
          selectedPatientEmail={selectedPatientEmail}
          selectedPatientName={selectedPatient?.name}
          appointmentType={selectedAppointmentType || undefined}
          selectedTimeZone={selectedTimeZone}
          patientTimezone={patientTimezone}
          dstDates={calBooking.dstDates}
          onConfirm={handleBookingConfirm}
        />
      )}
      {templateModal && (
        <CreateWorkRangeModal
          open={true}
          onClose={() => setTemplateModal(null)}
          date={templateModal.date}
          startTime={templateModal.startTime}
          endTime={templateModal.endTime}
          existingSchedules={template.filter(t => t.weekday === templateModal.weekdayIdx && t.active).map(t => ({
            id: t.id,
            startTime: t.startTime,
            endTime: t.endTime
          }))}
          allSchedules={template.filter(t => t.active).map(t => ({
            id: t.id,
            startTime: t.startTime,
            endTime: t.endTime,
            weekday: t.weekday
          }))}
          onSave={handleCreateWorkRangeModalSave}
        />
      )}
      {editModal && (
        <EditWorkRangeModal
          open={true}
          onClose={() => setEditModal(null)}
          scheduleId={editModal.id}
          weekdayIdx={editModal.weekday}
          startTime={editModal.startTime}
          endTime={editModal.endTime}
          scheduleType={editModal.scheduleType ?? 'standard'}
          existingSchedules={
            editMode && panelPreviewSchedules
              ? panelPreviewSchedules
                  .filter(s => s.weekday === editModal.weekday)
                  .map(s => ({ id: s.id, startTime: s.startTime, endTime: s.endTime }))
              : template.filter(t => t.weekday === editModal.weekday && t.active)
                  .map(t => ({ id: t.id, startTime: t.startTime, endTime: t.endTime }))
          }
          onDelete={handleEditWorkRangeModalDelete}
          onSave={handleEditWorkRangeModalSave}
        />
      )}
      {editSpecificDayModal && (() => {
        const sdDate = editSpecificDayModal.date;
        const [sdY, sdM, sdD] = sdDate.split('-').map(Number);
        const sdJsDay = new Date(sdY, sdM - 1, sdD).getDay();
        const sdWeekdayIdx = ((sdJsDay + 6) % 7);
        const DAY_NAMES_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const slotsForDate = dateSpecificSlots.filter(s => s.date === sdDate);
        const existingForModal = slotsForDate.map((s, i) => ({
          id: `specific-${i}` as string | number,
          startTime: s.startTime,
          endTime: s.endTime,
        }));
        const currentIdx = slotsForDate.findIndex(
          s => s.startTime === editSpecificDayModal.startTime && s.endTime === editSpecificDayModal.endTime
        );
        const scheduleId = currentIdx >= 0 ? `specific-${currentIdx}` : 'specific-0';
        return (
          <EditWorkRangeModal
            open={true}
            onClose={() => setEditSpecificDayModal(null)}
            scheduleId={scheduleId}
            weekdayIdx={sdWeekdayIdx}
            dateLabel={`${DAY_NAMES_FULL[sdJsDay]} ${sdD.toString().padStart(2, '0')}/${sdM.toString().padStart(2, '0')}`}
            startTime={editSpecificDayModal.startTime}
            endTime={editSpecificDayModal.endTime}
            scheduleType={editSpecificDayModal.scheduleType ?? 'standard'}
            existingSchedules={existingForModal}
            onDelete={handleEditSpecificDayModalDelete}
            onSave={handleEditSpecificDayModalSave}
          />
        );
      })()}
      <LargeLoading
        open={showLoadingModal}
        message="Configurando horario"
      />
      <LargeLoading
        open={isBooking}
        message="Agendando sesión"
      />
      <SessionDetailsModal
        key={selectedSession?.id}
        open={isSessionModalOpen}
        onClose={() => { setDeepLinkSessionView('details'); handleCloseSessionModal(); }}
        session={selectedSession}
        initialView={deepLinkSessionView}
        onCancel={handleCancelSession}
        onReschedule={handleRescheduleSession}
        psychologistId={psychologistId || undefined}
        therapistTimezone={selectedTimeZone}
        onRefreshSession={handleRefreshScheduledSession}
        onViewProcess={onSessionViewProcess && selectedSession ? () => onSessionViewProcess(selectedSession) : undefined}
        onStartReschedule={(session) => {
          setIsSessionModalOpen(false);
          selectPatient('');
          setSelectedAppointmentTypeId('');
          setSelectedAppointmentType(null);
          calReschedule.setReschedulingSession(null);
          setIsPreparingReschedule(true);
          setTimeout(() => {
            setIsPreparingReschedule(false);
            setReschedulingSession(session);
          }, 2500);
        }}
      />
      <LargeLoading
        open={isRescheduling}
        message="Reprogramando sesión"
      />
      <LargeLoading
        open={isPreparingReschedule}
        message="Preparando reprogramación"
      />
      <LargeLoading
        open={isCancelling}
        message="Cancelando sesión"
      />
      <LargeLoading
        open={isExitingEdit}
        message="Volviendo a la agenda"
      />
      <DayLimitModal
        open={showDayLimitModal}
        onClose={() => setShowDayLimitModal(false)}
      />
      <UnblockConfirmModal
        data={unblockConfirm}
        onClose={() => setUnblockConfirm(null)}
        onConfirm={handleUnblockConfirmed}
      />

      <RescheduleSummaryModal
        open={rescheduleSummaryOpen}
        onClose={() => setRescheduleSummaryOpen(false)}
        totalMinutes={reschedule7DaysData.totalMinutes}
        reservedMinutes={rescheduleReservedMinutes}
        availableMinutes={rescheduleAvailableMinutes}
        slots={reschedule7DaysData.slots}
        onConfigureSchedules={() => {
          setRescheduleSummaryOpen(false);
          setDefaultPanelScheduleType('reschedule_only');
          onEditModeToggle?.();
        }}
      />

      {/* Policy info modal — triggered from the WorkSchedulePanel CTA or the dropdown */}
      <RescheduleInfoModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        onOpenMyStats={() => {
          setInfoModalOpen(false);
          setMyStatsModalOpen(true);
        }}
      />

      {/* "Mis horarios de reprogramación" — stats modal opened from the info modal */}
      <RescheduleMyStatsModal
        open={myStatsModalOpen}
        onClose={() => setMyStatsModalOpen(false)}
        totalMinutes={reschedule7DaysData.totalMinutes}
        reservedMinutes={rescheduleReservedMinutes}
        availableMinutes={rescheduleAvailableMinutes}
        slots={rescheduleEnhancedSlots}
        onConfigureSchedules={() => {
          setMyStatsModalOpen(false);
          setDefaultPanelScheduleType('reschedule_only');
          // Only toggle edit mode if the panel isn't already open
          if (!editMode) onEditModeToggle?.();
        }}
      />

      {/* Celebration modal after first completed session */}
      <FirstSessionWelcomeModal
        open={firstSessionWelcomeOpen}
        onClose={() => {
          setFirstSessionWelcomeOpen(false);
          // Marcar "visto" en localStorage (cache) y en DB para sobrevivir entre dispositivos
          localStorage.setItem(FIRST_SESSION_WELCOME_KEY, '1');
          updatePlatformPreferences({ first_session_welcome_seen: true }).catch(() => { /* silencioso */ });
        }}
      />

      {/* Mock RescheduleConfirmModal — opened via psi-show-mock-reschedule-confirm event (E2E / onboarding) */}
      {mockRescheduleConfirmVisible && (
        <RescheduleConfirmModal
          open={true}
          onClose={() => setMockRescheduleConfirmVisible(false)}
          onConfirm={() => setMockRescheduleConfirmVisible(false)}
          confirming={false}
          sessionInfo={{
            patient: 'Natali Dominguez',
            currentDate: (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })(),
            currentTime: '11:00',
            currentEndTime: '12:00',
            duration: 60,
            therapyType: 'Individual',
          }}
          newDate={(() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().slice(0, 10); })()}
          newTime="13:00"
          newEndTime="14:00"
          therapistTimezone={selectedTimeZone}
          patientTimezone="Europe/Madrid"
          patientCurrentTime="16:00"
          patientCurrentEndTime="17:00"
          patientNewTime="18:00"
          patientNewEndTime="19:00"
          patientCountryCode="ES"
        />
      )}
    </div>
  );
};

export default WeeklyPsychologistCalendar;
